import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import {
  canAccessMenuKey,
  isAdminUser,
  readMatrixFromStorage,
  sanitizeNavigationPermissionMatrix,
  NAVIGATION_PERMISSION_UPDATED_EVENT,
  type NavigationPermissionMatrix
} from '../lib/navigationPolicy';
import {
  CheckCircle,
  RefreshCw,
  FolderOpen,
  GitPullRequest,
  Link2,
  Plus,
  Pause,
  Play,
  Trash2,
  Undo2,
  ArrowLeft,
  Pencil
} from 'lucide-react';
import toast from 'react-hot-toast';
import { describeFintsTanProcedure } from '../utils/fintsTanHints';
import FintsTanChallengeModal, { type FintsTanMethodOption } from './FintsTanChallengeModal';
import PaymentDemandStudentPickerModal from './PaymentDemandStudentPickerModal';
import PaymentStudentMultiSelect, {
  buildDemandsStudentsPayload,
  type SchuelerRow as PickerSchuelerRow
} from './PaymentStudentMultiSelect';
import {
  Badge,
  type BadgeVariant,
  Button,
  EmptyTableRow,
  ListToolbar,
  SortableTh,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea,
  Tile,
  TileContent,
  TileDescription,
  Modal
} from './ui';

/** Cookie-Session wie Einstellungen — nicht nur Bearer aus localStorage (HttpOnly-Login → leer → 401 und fälschlich „Backend offline“). */
function sessionFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  return globalThis.fetch(input, { ...init, credentials: 'include' });
}

// Safe converter for amounts coming as number or string
const toAmount = (val: any): number => {
  if (typeof val === 'number') return isFinite(val) ? val : 0;
  const n = parseFloat((val ?? '0').toString().replace(',', '.'));
  return isNaN(n) ? 0 : n;
};

/**
 * Bank-Verwendungszweck für die Anzeige weicher umbrechen:
 * Pipes sichtbar machen, LR+Referenzcode ohne Trennzeichen aufdröseln, Wortgrenzen helfen.
 */
function softenBankPurposeForDisplay(raw: string): string {
  let s = String(raw || '').trim();
  if (!s) return '';
  s = s.replace(/\s*\|\s*/g, ' | ');
  s = s.replace(/^LR([A-Z2-9]{8,12})(?=[A-Za-zÄÖÜ])/i, 'LR $1 ');
  s = s.replace(/([^A-Za-zäöüÄÖÜ0-9])LR([A-Z2-9]{8,12})(?=[A-Za-zÄÖÜ])/gi, '$1LR $2 ');
  s = s.replace(/([a-zäöüß])([A-ZÄÖÜ][a-zäöüß]+)/g, '$1 $2');
  s = s.replace(/([A-Za-zäöüß])([0-9]{4,})/g, '$1 $2');
  s = s.replace(/([0-9])([A-Za-zäöüÄÖÜ]{3,})/g, '$1 $2');
  return s.replace(/\s+/g, ' ').trim();
}

function formatPaymentListDateTime(iso?: string | null): string {
  if (!iso) return '–';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '–' : d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

/** Große Auswahlen in mehrere API-Aufrufe splitten (Timeouts / stabiler Betrieb). */
const DEMAND_BATCH_CHUNK_SIZE = 400;

function chunkNumberIds(ids: number[], size: number): number[][] {
  const out: number[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    out.push(ids.slice(i, i + size));
  }
  return out;
}

interface PaymentTransaction {
  id: string;
  status: string;
  amount: {
    currency_code: string;
    value: string;
  };
  payer: {
    email_address: string;
    name: {
      given_name: string;
      surname: string;
    };
  };
  create_time: string;
  update_time: string;
}

interface GiroAllocationSourceLine {
  kind?: string;
  label_de?: string;
  line_de: string;
  actor_label?: string | null;
  hint_de?: string;
  amount?: number;
}

interface GiroTransaction {
  id: number;
  external_id: string;
  booking_date?: string;
  amount: number;
  currency: string;
  counterpart_name?: string;
  purpose?: string;
  match_method?: string;
  allocation_state?: string;
  unallocated_surplus?: number;
  reference_text?: string;
  linked_campaign_titles?: string | null;
  linked_recipient_names?: string | null;
  linked_creator_usernames?: string | null;
  linked_reference_codes?: string | null;
  allocation_count?: number;
  /** Je verbuchter Teilzuordnung: Auto vs. Manuell vs. Freigabe */
  allocation_sources?: GiroAllocationSourceLine[];
  allocation_source_summary?: string;
}

interface PaymentCampaign {
  id: number;
  title: string;
  description?: string;
  status: string;
  starts_at?: string | null;
  due_at?: string;
  created_at?: string;
  created_by_username?: string | null;
  demand_count?: number;
  total_amount?: number;
  open_amount?: number;
  /** JSON aus DB — Standardbetrag, Mahnung, Assistent */
  metadata?: Record<string, unknown> | string | null;
}

interface PaymentDemand {
  id: number;
  campaign_id: number;
  campaign_title?: string;
  recipient_name?: string;
  recipient_email?: string;
  amount_total: number;
  amount_open: number;
  currency: string;
  reference_code: string;
  purpose_line?: string;
  status: string;
  due_at?: string;
  created_at?: string;
  created_by_username?: string | null;
  last_received_at?: string | null;
  last_received_amount?: number;
  has_unclear_flag?: number;
  /** Letzte manuelle Barbuchung (stornierbar) */
  latest_manual_cash_allocation_id?: number | null;
  latest_manual_cash_amount?: number | null;
  manual_cash_entry_count?: number | null;
}

interface AllocationSuggestion {
  id: number;
  demand_id: number;
  giro_transaction_id: number;
  amount: number;
  confidence?: number;
  match_method: string;
  campaign_title?: string;
  recipient_name?: string;
  reference_code?: string;
  purpose?: string;
  counterpart_name?: string;
}

/** Zeilen im Modal «Zahlung Forderungen zuordnen» */
interface BankGiroEditLine {
  demand_id: number;
  amount: string;
  label: string;
}

const BANK_DEMAND_PICKER_MAX_ROWS = 500;

function demandStatusLabelDe(st: string): string {
  switch (String(st || '').toLowerCase()) {
    case 'open':
      return 'Offen';
    case 'partially_paid':
      return 'Teilbezahlt';
    case 'overpaid':
      return 'Überzahlt';
    default:
      return st || '–';
  }
}

function filterBankDemandsForPicker(
  pool: PaymentDemand[],
  search: string,
  campaignFilter: string
): PaymentDemand[] {
  let list = pool;
  if (campaignFilter && campaignFilter !== 'all') {
    const cid = Number(campaignFilter);
    if (Number.isFinite(cid)) {
      list = list.filter((d) => d.campaign_id === cid);
    }
  }
  const t = search.trim().toLowerCase();
  if (!t) return list;
  return list.filter((d) => {
    const name = (d.recipient_name || '').toLowerCase();
    const ref = (d.reference_code || '').toLowerCase();
    const camp = (d.campaign_title || '').toLowerCase();
    const purpose = (d.purpose_line || '').toLowerCase();
    const amt = `${toAmount(d.amount_open).toFixed(2)}`.toLowerCase();
    const cur = (d.currency || '').toLowerCase();
    const idStr = String(d.id);
    const stLabel = demandStatusLabelDe(d.status).toLowerCase();
    return (
      name.includes(t) ||
      ref.includes(t) ||
      camp.includes(t) ||
      purpose.includes(t) ||
      amt.includes(t) ||
      cur.includes(t) ||
      idStr.includes(t) ||
      stLabel.includes(t)
    );
  });
}

/** Auswahl einer Forderung aus dem Pool offener/teiloffener Forderungen (Suche + Vorgangsfilter). */
function BankDemandPicker({
  demandId,
  label,
  candidates,
  loading,
  onSelect,
  onClear
}: {
  demandId: number;
  label: string;
  candidates: PaymentDemand[];
  loading: boolean;
  onSelect: (d: PaymentDemand) => void;
  onClear: () => void;
}) {
  const [q, setQ] = useState('');
  const [campaignId, setCampaignId] = useState<string>('all');

  const campaignOptions = useMemo(() => {
    const m = new Map<number, string>();
    for (const d of candidates) {
      if (!m.has(d.campaign_id)) {
        m.set(d.campaign_id, d.campaign_title || `Vorgang #${d.campaign_id}`);
      }
    }
    return Array.from(m.entries()).sort((a, b) =>
      (a[1] || '').localeCompare(b[1] || '', 'de', { sensitivity: 'base' })
    );
  }, [candidates]);

  const filtered = useMemo(
    () => filterBankDemandsForPicker(candidates, q, campaignId),
    [candidates, q, campaignId]
  );
  const visible = filtered.slice(0, BANK_DEMAND_PICKER_MAX_ROWS);
  const truncated = filtered.length > BANK_DEMAND_PICKER_MAX_ROWS;

  if (demandId > 0) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-800 dark:text-gray-200">{label || `Forderung #${demandId}`}</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            onClear();
            setQ('');
            setCampaignId('all');
          }}
        >
          Ändern
        </Button>
      </div>
    );
  }

  return (
    <div className="min-w-0 flex-1 space-y-2">
      {loading ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">Lade offene und teilbezahlte Forderungen…</p>
      ) : candidates.length === 0 ? (
        <p className="text-xs text-amber-800 dark:text-amber-200">
          Keine zuordenbaren Forderungen (nichts mit offenem Betrag) oder keine Berechtigung.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
            <input
              className="input-field min-w-0 flex-1 py-1.5 text-sm"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suchen: Name, Referenz, Vorgang, Verwendungszweck, Betrag…"
              autoComplete="off"
              aria-label="Forderungen durchsuchen"
            />
            <select
              className="input-field shrink-0 py-1.5 text-sm sm:w-56"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              aria-label="Nach Zahlungsvorgang filtern"
            >
              <option value="all">Alle Vorgänge ({campaignOptions.length})</option>
              {campaignOptions.map(([id, title]) => (
                <option key={id} value={String(id)}>
                  {title}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {filtered.length} von {candidates.length} Forderungen
            {truncated
              ? ` — es werden nur die ersten ${BANK_DEMAND_PICKER_MAX_ROWS} Treffer angezeigt; Suche eingrenzen.`
              : ''}
          </p>
          <ul
            className="max-h-60 overflow-auto rounded-md border border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-900"
            role="listbox"
            aria-label="Forderungen zur Auswahl"
          >
            {visible.map((d) => (
              <li key={d.id} role="option">
                <button
                  type="button"
                  className="w-full border-b border-gray-100 px-3 py-2 text-left text-sm last:border-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                  onClick={() => {
                    onSelect(d);
                    setQ('');
                    setCampaignId('all');
                  }}
                >
                  <span className="font-medium text-gray-900 dark:text-gray-100">{d.recipient_name || '–'}</span>
                  <span className="mt-0.5 block text-xs text-gray-600 dark:text-gray-300">
                    <span className="tabular-nums">{d.reference_code}</span>
                    {' · '}
                    <span>{d.campaign_title || '–'}</span>
                  </span>
                  <span className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>
                      Offen{' '}
                      <span className="tabular-nums font-medium text-gray-700 dark:text-gray-200">
                        {toAmount(d.amount_open).toFixed(2)} {d.currency || 'EUR'}
                      </span>
                    </span>
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide dark:bg-gray-800">
                      {demandStatusLabelDe(d.status)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

interface LeadershipTotals {
  demand_count: number;
  amount_total: number;
  amount_open: number;
  overdue_open_amount: number;
  open_count: number;
  paid_count: number;
}

interface LeadershipCampaignRow {
  id: number;
  title: string;
  demand_count: number;
  amount_total: number;
  amount_open: number;
  overdue_open_amount: number;
}

const CAMPAIGN_STATUS_LABEL: Record<string, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  closed: 'Abgeschlossen',
  cancelled: 'Abgebrochen'
};

function campaignStatusBadgeVariant(status: string): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'draft':
      return 'warning';
    case 'closed':
      return 'neutral';
    case 'cancelled':
      return 'danger';
    default:
      return 'neutral';
  }
}

function giroAllocationStateLabel(st: string): string {
  switch (String(st || '').toLowerCase()) {
    case 'unprocessed':
      return 'Offen';
    case 'suggested':
      return 'Vorschlag';
    case 'partially_allocated':
      return 'Teilweise';
    case 'fully_allocated':
      return 'Verbucht';
    case 'ignored':
      return 'Ignoriert';
    default:
      return st || '–';
  }
}

function financeAuditActionLabelDe(action: string): string {
  switch (String(action || '')) {
    case 'giro_allocations_replace':
      return 'Zuordnungen manuell gespeichert';
    case 'allocation_delete':
      return 'Einzelzuordnung entfernt';
    case 'allocation_approve':
      return 'Vorschlag freigegeben';
    case 'allocation_reject':
      return 'Vorschlag abgelehnt';
    default:
      return action || '–';
  }
}

function giroAllocationBadgeVariant(st: string): BadgeVariant {
  switch (String(st || '').toLowerCase()) {
    case 'fully_allocated':
      return 'success';
    case 'partially_allocated':
      return 'info';
    case 'suggested':
      return 'warning';
    case 'unprocessed':
      return 'neutral';
    case 'ignored':
      return 'danger';
    default:
      return 'neutral';
  }
}

function demandStatusBadgeVariant(st: string): BadgeVariant {
  switch (st) {
    case 'open':
      return 'warning';
    case 'partially_paid':
      return 'info';
    case 'paid':
      return 'success';
    case 'overpaid':
      return 'info';
    case 'paused':
      return 'neutral';
    case 'cancelled':
      return 'danger';
    case 'refunded':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function parsePaymentCampaignMetadata(m: unknown): Record<string, unknown> {
  if (m && typeof m === 'object' && !Array.isArray(m)) return m as Record<string, unknown>;
  if (typeof m === 'string' && m.trim()) {
    try {
      return JSON.parse(m) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

const PaymentManager: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'campaigns' | 'matching'>('campaigns');
  const [backendOnline, setBackendOnline] = useState(false);
  const [newCampaignTitle, setNewCampaignTitle] = useState('');
  const [newCampaignDueAt, setNewCampaignDueAt] = useState('');
  const [newCampaignAmount, setNewCampaignAmount] = useState('');
  const [newCampaignDescription, setNewCampaignDescription] = useState('');
  /** Nach Anlegen (nur mit Schüler-Forderungen): Erstversand & Mahntermin */
  const [createSendEmail, setCreateSendEmail] = useState(true);
  const [createSendPdf, setCreateSendPdf] = useState(false);
  const [createAutoRemind, setCreateAutoRemind] = useState(false);
  const [createAutoRemindAt, setCreateAutoRemindAt] = useState('');
  const [createCampaignStudentIds, setCreateCampaignStudentIds] = useState<string[]>([]);
  const [pickerStudents, setPickerStudents] = useState<PickerSchuelerRow[]>([]);
  const studentPickCache = useRef<Map<string, PickerSchuelerRow>>(new Map());
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  /** Eigener Bildschirm für Vorgang anlegen (analog Forderungen-Detail). */
  const [campaignCreateOpen, setCampaignCreateOpen] = useState(false);
  const [demandPickerOpen, setDemandPickerOpen] = useState(false);
  const [demandPickerCampaign, setDemandPickerCampaign] = useState<{
    id: number;
    title: string;
    due_at?: string | null;
  } | null>(null);
  const [demandBusyId, setDemandBusyId] = useState<number | null>(null);
  const [demandPdfTemplate, setDemandPdfTemplate] = useState<'professional' | 'playful' | 'simple'>(
    'professional'
  );
  /** Vorgang bearbeiten (Forderungen-Ansicht) */
  const [campaignEditOpen, setCampaignEditOpen] = useState(false);
  const [campaignEditTitle, setCampaignEditTitle] = useState('');
  const [campaignEditDueAt, setCampaignEditDueAt] = useState('');
  const [campaignEditDescription, setCampaignEditDescription] = useState('');
  const [campaignEditStatus, setCampaignEditStatus] = useState<string>('active');
  const [campaignEditAmount, setCampaignEditAmount] = useState('');
  const [campaignEditAutoRemind, setCampaignEditAutoRemind] = useState(false);
  const [campaignEditAutoRemindAt, setCampaignEditAutoRemindAt] = useState('');
  const [campaignScopeModalOpen, setCampaignScopeModalOpen] = useState(false);
  const campaignEditBaseline = useRef<{
    title: string;
    dueAt: string;
    description: string;
    status: string;
    amount: string;
    autoRemind: boolean;
    autoRemindAt: string;
    pdfTemplate: 'professional' | 'playful' | 'simple';
  } | null>(null);

  const [campaignListSearch, setCampaignListSearch] = useState('');
  const [campaignListStatus, setCampaignListStatus] = useState('');
  const [campaignListSort, setCampaignListSort] = useState<
    'created_at' | 'title' | 'due_at' | 'status' | 'open_amount' | 'demand_count' | 'total_amount'
  >('created_at');
  const [campaignListOrder, setCampaignListOrder] = useState<'asc' | 'desc'>('desc');

  const [demandListSearch, setDemandListSearch] = useState('');
  const [demandListStatus, setDemandListStatus] = useState('');
  const [demandListSort, setDemandListSort] = useState<
    | 'due_at'
    | 'created_at'
    | 'amount_open'
    | 'amount_total'
    | 'recipient_name'
    | 'status'
    | 'reference_code'
    | 'campaign_title'
  >('due_at');
  const [demandListOrder, setDemandListOrder] = useState<'asc' | 'desc'>('asc');

  /** Filter Kontoauszug / Zuordnungen (Tab «Zuordnungen») */
  const [bankPreset, setBankPreset] = useState<string>('all');
  const [bankCampaignId, setBankCampaignId] = useState<string>('');
  const [bankCreator, setBankCreator] = useState('');
  const [bankStudentQ, setBankStudentQ] = useState('');
  const [bankTextQ, setBankTextQ] = useState('');
  const [bankState, setBankState] = useState<string>('');
  const [bankPage, setBankPage] = useState(1);
  const bankPageSize = 50;
  const [bankGiroEditTx, setBankGiroEditTx] = useState<GiroTransaction | null>(null);
  const [bankGiroEditLines, setBankGiroEditLines] = useState<BankGiroEditLine[]>([]);
  const [bankGiroEditLoading, setBankGiroEditLoading] = useState(false);
  const [bankGiroEditSaving, setBankGiroEditSaving] = useState(false);
  /** Offene/teiloffene Forderungen für manuelle Giro-Zuordnung (einmal pro geöffnetem Modal) */
  const [bankGiroOpenDemandsPool, setBankGiroOpenDemandsPool] = useState<PaymentDemand[]>([]);
  const [bankGiroOpenDemandsLoading, setBankGiroOpenDemandsLoading] = useState(false);
  /** Papierkorb-Ansichten (Vorgänge / einzelne Forderungen) */
  const [campaignTrashOnly, setCampaignTrashOnly] = useState(false);
  const [demandTrashOnly, setDemandTrashOnly] = useState(false);
  const [bankFinanceAuditTx, setBankFinanceAuditTx] = useState<GiroTransaction | null>(null);
  const [bankFinanceAuditLoading, setBankFinanceAuditLoading] = useState(false);
  const [bankFinanceAuditEntries, setBankFinanceAuditEntries] = useState<
    Array<{
      id: number;
      action: string;
      before_json: unknown;
      after_json: unknown;
      created_at: string;
      ip_address?: string | null;
      actor_username?: string | null;
    }>
  >([]);

  const [syncingGiro, setSyncingGiro] = useState(false);
  const [tanToken, setTanToken] = useState<string | null>(null);
  const [tanChallengeText, setTanChallengeText] = useState('');
  const [tanValue, setTanValue] = useState('');
  const [tanChallengeMediaB64, setTanChallengeMediaB64] = useState<string | null>(null);
  const [tanChallengeMediaMime, setTanChallengeMediaMime] = useState<string | null>(null);
  const [tanChallengeHhduc, setTanChallengeHhduc] = useState<string | null>(null);
  const [tanSecurityFunction, setTanSecurityFunction] = useState<string | null>(null);
  const [tanDecoupled, setTanDecoupled] = useState(false);
  const [tanMethodOptions, setTanMethodOptions] = useState<FintsTanMethodOption[]>([]);
  const [selectedTanMethod, setSelectedTanMethod] = useState('');
  const [tanProcessCode, setTanProcessCode] = useState<string | null>(null);
  const [tanBankMediumName, setTanBankMediumName] = useState<string | null>(null);
  
  const queryClient = useQueryClient();
  const [permissionMatrix, setPermissionMatrix] = useState<NavigationPermissionMatrix>(() => readMatrixFromStorage());

  useEffect(() => {
    const sync = () => setPermissionMatrix(readMatrixFromStorage());
    const onUpdate = (e: Event) => {
      const d = (e as CustomEvent<{ matrix?: NavigationPermissionMatrix }>).detail;
      if (d?.matrix) {
        setPermissionMatrix(d.matrix);
        return;
      }
      sync();
    };
    window.addEventListener('storage', sync);
    window.addEventListener(NAVIGATION_PERMISSION_UPDATED_EVENT, onUpdate as EventListener);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(NAVIGATION_PERMISSION_UPDATED_EVENT, onUpdate as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!bankGiroEditTx) {
      setBankGiroOpenDemandsPool([]);
      setBankGiroOpenDemandsLoading(false);
      return;
    }
    let cancelled = false;
    setBankGiroOpenDemandsLoading(true);
    void (async () => {
      try {
        const r = await sessionFetch(
          '/api/payments/v2/demands?status_in=open,partially_paid,overpaid&min_open=1&limit=5000&sort=recipient_name&order=asc'
        );
        const j = await r.json();
        if (cancelled) return;
        setBankGiroOpenDemandsPool(Array.isArray(j.data?.demands) ? j.data.demands : []);
      } catch {
        if (!cancelled) {
          setBankGiroOpenDemandsPool([]);
          toast.error('Forderungsliste für die Zuordnung konnte nicht geladen werden');
        }
      } finally {
        if (!cancelled) setBankGiroOpenDemandsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bankGiroEditTx]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await api.get('/settings/navigation-permissions');
        if (!mounted || !res?.data?.success) return;
        setPermissionMatrix(
          sanitizeNavigationPermissionMatrix((res.data.data?.matrix || {}) as NavigationPermissionMatrix)
        );
      } catch {
        /* localStorage/Storage-Event reicht */
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [user?.username]);

  const normalizedRoles = useMemo(
    () =>
      (user?.allRoles || user?.roles || [user?.role] || [])
        .filter(Boolean)
        .map((role) => String(role || '').toLowerCase()),
    [user]
  );
  const hasRole = (roleName: string) => normalizedRoles.includes(roleName);
  const capZahlungLehrer = useMemo(
    () =>
      canAccessMenuKey({
        menuKey: 'sonstiges.zahlungen.lehrer',
        user,
        matrix: permissionMatrix
      }),
    [user, permissionMatrix]
  );
  const capZahlungSekretariat = useMemo(
    () =>
      canAccessMenuKey({
        menuKey: 'sonstiges.zahlungen.sekretariat',
        user,
        matrix: permissionMatrix
      }),
    [user, permissionMatrix]
  );
  const capZahlungSchulleitung = useMemo(
    () =>
      canAccessMenuKey({
        menuKey: 'sonstiges.zahlungen.schulleitung',
        user,
        matrix: permissionMatrix
      }),
    [user, permissionMatrix]
  );
  const hasAnyPaymentCapability =
    isAdminUser(user) || capZahlungLehrer || capZahlungSekretariat || capZahlungSchulleitung;

  const rolePersona: 'teacher' | 'secretariat' | 'leadership' = hasRole('sekretariat')
    ? 'secretariat'
    : hasRole('admin') || hasRole('schulleitung') || hasRole('abteilungsleiter') || hasRole('abteilungsleitung')
      ? 'leadership'
      : 'teacher';

  const paymentPersona: 'teacher' | 'secretariat' | 'leadership' = useMemo(() => {
    if (isAdminUser(user)) return 'leadership';
    if (rolePersona === 'secretariat' && capZahlungSekretariat) return 'secretariat';
    if (rolePersona === 'leadership' && capZahlungSchulleitung) return 'leadership';
    if (rolePersona === 'teacher' && capZahlungLehrer) return 'teacher';
    if (rolePersona === 'secretariat' && capZahlungLehrer) return 'teacher';
    if (rolePersona === 'leadership' && capZahlungLehrer) return 'teacher';
    return 'teacher';
  }, [user, rolePersona, capZahlungLehrer, capZahlungSekretariat, capZahlungSchulleitung]);

  const isTeacherPersona = paymentPersona === 'teacher';
  const canUseRawWorkflows = paymentPersona !== 'teacher';

  const availableTabs = useMemo<Array<'overview' | 'transactions' | 'campaigns' | 'matching'>>(() => {
    if (!hasAnyPaymentCapability) return [];
    if (paymentPersona === 'secretariat') return ['overview', 'transactions', 'campaigns', 'matching'];
    if (paymentPersona === 'leadership') return ['overview', 'campaigns', 'matching'];
    return ['campaigns'];
  }, [paymentPersona, hasAnyPaymentCapability]);

  const campaignUiActive = useMemo(
    () =>
      activeTab === 'campaigns' ||
      ((paymentPersona === 'leadership' || paymentPersona === 'secretariat') && activeTab === 'overview'),
    [activeTab, paymentPersona]
  );

  useEffect(() => {
    if (availableTabs.length === 0) return;
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [activeTab, availableTabs]);

  useEffect(() => {
    if (activeTab !== 'campaigns') setCampaignCreateOpen(false);
  }, [activeTab]);

  useEffect(() => {
    setBankPage(1);
  }, [bankPreset, bankCampaignId, bankCreator, bankStudentQ, bankTextQ, bankState]);

  useEffect(() => {
    if (selectedCampaignId != null) setCampaignCreateOpen(false);
  }, [selectedCampaignId]);

  const tanProcedureHint = useMemo(() => {
    const sel = tanMethodOptions.find((m) => m.id === selectedTanMethod);
    const mtp = sel?.tan_process != null ? String(sel.tan_process).trim().slice(0, 1) : null;
    return describeFintsTanProcedure({
      decoupled: tanDecoupled,
      tanProcessCode,
      hasChallengeImage: Boolean(tanChallengeMediaB64 && tanChallengeMediaMime?.startsWith('image/')),
      methodTanProcess: mtp
    });
  }, [
    tanDecoupled,
    tanProcessCode,
    tanChallengeMediaB64,
    tanChallengeMediaMime,
    tanMethodOptions,
    selectedTanMethod
  ]);

  const applyTanChallengePayload = (d: Record<string, unknown>) => {
    const methods = Array.isArray(d.tan_methods)
      ? (d.tan_methods as FintsTanMethodOption[])
      : [];
    setTanMethodOptions(methods);
    const cur = String(d.tan_security_function || '').trim();
    const fallback = methods[0]?.id ? String(methods[0].id) : '';
    setSelectedTanMethod(cur || fallback);
    setTanSecurityFunction(cur || null);
    const pc = String(d.tan_process_code ?? '').trim().slice(0, 1);
    setTanProcessCode(pc || null);
    const bm = String(d.tan_medium_name ?? '').trim();
    setTanBankMediumName(bm || null);
  };

  // Prüfe Backend-Status
  useEffect(() => {
    const checkBackend = async () => {
      try {
        const response = await sessionFetch('/api/payments/payment-requests?page=1&pageSize=1');
        setBackendOnline(response.ok);
      } catch {
        setBackendOnline(false);
      }
    };
    checkBackend();
    const interval = setInterval(checkBackend, 10000); // Alle 10 Sekunden prüfen
    return () => clearInterval(interval);
  }, []);

  const { data: transactions, isLoading: loadingTransactions } = useQuery<PaymentTransaction[]>(
    'paypal-transactions',
    () => sessionFetch('/api/payments/transactions').then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    }).then(data => data.data?.transactions || []),
    {
      retry: 1,
      enabled: backendOnline && hasAnyPaymentCapability && canUseRawWorkflows && activeTab === 'transactions',
      onError: (error) => {
        console.error('Transactions API Error:', error);
      }
    }
  );

  const { data: giroTransactions, isLoading: loadingGiroTransactions } = useQuery<GiroTransaction[]>(
    'giro-transactions',
    () =>
      sessionFetch('/api/payments/giro/transactions').then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      }).then((data) => data.data?.transactions || []),
    {
      retry: 1,
      enabled: backendOnline && hasAnyPaymentCapability && canUseRawWorkflows && activeTab === 'transactions',
      onError: (error) => {
        console.error('Giro transactions API error:', error);
      }
    }
  );

  const { data: campaigns, isLoading: loadingCampaigns } = useQuery<PaymentCampaign[]>(
    [
      'payment-campaigns',
      'mine',
      campaignListSearch,
      campaignListStatus,
      campaignListSort,
      campaignListOrder,
      campaignTrashOnly
    ],
    () => {
      const sp = new URLSearchParams();
      sp.set('scope', 'mine');
      if (campaignTrashOnly) sp.set('trash', '1');
      if (campaignListSearch.trim()) sp.set('q', campaignListSearch.trim());
      if (campaignListStatus) sp.set('status', campaignListStatus);
      sp.set('sort', campaignListSort);
      sp.set('order', campaignListOrder);
      const qs = sp.toString();
      return sessionFetch(`/api/payments/v2/campaigns?${qs}`).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      }).then((data) => data.data?.campaigns || []);
    },
    {
      retry: 1,
      enabled: backendOnline && hasAnyPaymentCapability && activeTab === 'campaigns',
      onError: (error) => {
        console.error('Campaigns API error:', error);
      }
    }
  );

  const needsSchoolCampaignMeta = useMemo(() => {
    if (!selectedCampaignId) return false;
    return !(campaigns || []).some((c) => c.id === selectedCampaignId);
  }, [campaigns, selectedCampaignId]);

  const { data: schoolCampaigns, isLoading: loadingSchoolCampaigns } = useQuery<PaymentCampaign[]>(
    [
      'payment-campaigns',
      'school',
      campaignListSearch,
      campaignListStatus,
      campaignListSort,
      campaignListOrder,
      campaignTrashOnly
    ],
    () => {
      const sp = new URLSearchParams();
      sp.set('scope', 'school');
      if (campaignTrashOnly) sp.set('trash', '1');
      if (campaignListSearch.trim()) sp.set('q', campaignListSearch.trim());
      if (campaignListStatus) sp.set('status', campaignListStatus);
      sp.set('sort', campaignListSort);
      sp.set('order', campaignListOrder);
      return sessionFetch(`/api/payments/v2/campaigns?${sp.toString()}`).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      }).then((data) => data.data?.campaigns || []);
    },
    {
      retry: 1,
      enabled:
        backendOnline &&
        hasAnyPaymentCapability &&
        (paymentPersona === 'leadership' || paymentPersona === 'secretariat') &&
        (activeTab === 'overview' || needsSchoolCampaignMeta),
      onError: (error) => {
        console.error('School campaigns API error:', error);
      }
    }
  );

  const displayedCampaigns = activeTab === 'overview' ? schoolCampaigns ?? [] : campaigns ?? [];
  const loadingDisplayedCampaigns = activeTab === 'overview' ? loadingSchoolCampaigns : loadingCampaigns;

  const { data: demands, isLoading: loadingDemands } = useQuery<PaymentDemand[]>(
    [
      'payment-demands',
      selectedCampaignId,
      demandTrashOnly,
      demandListSearch,
      demandListStatus,
      demandListSort,
      demandListOrder
    ],
    () => {
      const sp = new URLSearchParams();
      if (demandTrashOnly) {
        sp.set('trash', '1');
      } else if (selectedCampaignId) {
        sp.set('campaign_id', String(selectedCampaignId));
      }
      if (demandListSearch.trim()) sp.set('q', demandListSearch.trim());
      if (demandListStatus) sp.set('status', demandListStatus);
      sp.set('sort', demandListSort);
      sp.set('order', demandListOrder);
      const qs = sp.toString();
      return sessionFetch(`/api/payments/v2/demands${qs ? `?${qs}` : ''}`).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      }).then((data) => data.data?.demands || []);
    },
    {
      retry: 1,
      enabled:
        backendOnline &&
        hasAnyPaymentCapability &&
        (activeTab === 'campaigns' || activeTab === 'overview') &&
        (demandTrashOnly || selectedCampaignId != null),
      onError: (error) => {
        console.error('Demands API error:', error);
      }
    }
  );

  const { data: allocationSuggestions, isLoading: loadingSuggestions } = useQuery<AllocationSuggestion[]>(
    'allocation-suggestions',
    () =>
      sessionFetch('/api/payments/v2/match/suggestions').then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      }).then((data) => data.data?.suggestions || []),
    {
      retry: 1,
      enabled: backendOnline && hasAnyPaymentCapability && canUseRawWorkflows && activeTab === 'matching',
      onError: (error) => {
        console.error('Suggestions API error:', error);
      }
    }
  );

  const { data: bankFilterCampaigns } = useQuery<PaymentCampaign[]>(
    ['payment-campaigns', 'bank-filter'],
    () =>
      sessionFetch('/api/payments/v2/campaigns?scope=school&sort=title&order=asc')
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data) => data.data?.campaigns || []),
    {
      retry: 1,
      enabled:
        backendOnline &&
        hasAnyPaymentCapability &&
        canUseRawWorkflows &&
        activeTab === 'matching' &&
        (paymentPersona === 'leadership' || paymentPersona === 'secretariat'),
      onError: (error) => {
        console.error('Bank filter campaigns error:', error);
      }
    }
  );

  const { data: bankAllocationsData, isLoading: loadingBankAllocations } = useQuery<{
    transactions: GiroTransaction[];
    total: number;
    page: number;
    pageSize: number;
  }>(
    [
      'bank-allocations-table',
      bankPreset,
      bankCampaignId,
      bankCreator,
      bankStudentQ,
      bankTextQ,
      bankState,
      bankPage
    ],
    () => {
      const sp = new URLSearchParams();
      sp.set('credit_only', '1');
      sp.set('page', String(bankPage));
      sp.set('pageSize', String(bankPageSize));
      if (bankPreset && bankPreset !== 'all') sp.set('preset', bankPreset);
      if (bankCampaignId) sp.set('campaign_id', bankCampaignId);
      if (bankCreator.trim()) sp.set('demand_creator', bankCreator.trim());
      if (bankStudentQ.trim()) sp.set('student_q', bankStudentQ.trim());
      if (bankTextQ.trim()) sp.set('q', bankTextQ.trim());
      if (bankState) sp.set('state', bankState);
      return sessionFetch(`/api/payments/v2/bank-transactions?${sp.toString()}`)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data) => ({
          transactions: data.data?.transactions || [],
          total: Number(data.data?.total ?? 0),
          page: Number(data.data?.page ?? 1),
          pageSize: Number(data.data?.pageSize ?? bankPageSize)
        }));
    },
    {
      retry: 1,
      enabled: backendOnline && hasAnyPaymentCapability && canUseRawWorkflows && activeTab === 'matching',
      onError: (error) => {
        console.error('Bank transactions API error:', error);
      }
    }
  );
  const bankTransactions = bankAllocationsData?.transactions ?? [];
  const bankTransactionsTotal = bankAllocationsData?.total ?? 0;

  const { data: leadershipOverview } = useQuery<{
    totals: LeadershipTotals;
    campaigns: LeadershipCampaignRow[];
  }>(
    'payment-leadership-overview',
    () =>
      sessionFetch('/api/payments/v2/leadership/overview')
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data) => ({
          totals: data.data?.totals || {
            demand_count: 0,
            amount_total: 0,
            amount_open: 0,
            overdue_open_amount: 0,
            open_count: 0,
            paid_count: 0
          },
          campaigns: data.data?.campaigns || []
        })),
    {
      retry: 1,
      enabled: backendOnline && hasAnyPaymentCapability && paymentPersona === 'leadership' && activeTab === 'overview',
      onError: (error) => {
        console.error('Leadership overview API error:', error);
      }
    }
  );

  const startGiroSync = async () => {
    setSyncingGiro(true);
    setTanChallengeMediaB64(null);
    setTanChallengeMediaMime(null);
    setTanChallengeHhduc(null);
    setTanSecurityFunction(null);
    setTanDecoupled(false);
    setTanMethodOptions([]);
    setSelectedTanMethod('');
    setTanProcessCode(null);
    setTanBankMediumName(null);
    try {
      const response = await api.post('/payments/giro/fints/sync', {});
      const data = response.data;
      if (!data?.success) {
        throw new Error(data?.message || 'FinTS-Abruf fehlgeschlagen');
      }

      if (data.status === 'tan_required') {
        const d = data.data || {};
        setTanToken(d.tan_token || null);
        setTanChallengeText(d.challenge_text || 'TAN erforderlich');
        setTanChallengeMediaB64(d.challenge_media_base64 || null);
        setTanChallengeMediaMime(d.challenge_media_mime || null);
        setTanChallengeHhduc(d.challenge_hhduc || null);
        setTanDecoupled(!!d.decoupled);
        applyTanChallengePayload(d as Record<string, unknown>);
        toast(d.decoupled ? 'Bitte Freigabe in der Banking-App bestätigen.' : 'TAN benötigt. Bitte eingeben.');
        return;
      }

      toast.success(
        `Giro-Umsätze importiert: ${data?.data?.imported || 0}, auto-zugeordnet: ${data?.data?.matched || 0}, Vorschläge: ${data?.data?.suggested || 0}`
      );
      queryClient.invalidateQueries('giro-transactions');
    } catch (error: any) {
      toast.error(error?.message || 'FinTS-Abruf fehlgeschlagen');
    } finally {
      setSyncingGiro(false);
    }
  };

  const submitTan = async () => {
    if (!tanToken) {
      toast.error('Keine aktive TAN-Sitzung');
      return;
    }
    if (!tanDecoupled && !tanValue.trim()) {
      toast.error('Bitte TAN eingeben');
      return;
    }
    if (tanMethodOptions.length > 0 && !String(selectedTanMethod || '').trim()) {
      toast.error('Bitte TAN-Verfahren wählen');
      return;
    }
    setSyncingGiro(true);
    try {
      const tanPayload: Record<string, string> = {
        tan_token: tanToken,
        tan: tanDecoupled ? '' : tanValue.trim()
      };
      const sm = String(selectedTanMethod || '').trim();
      if (sm) tanPayload.fints_security_function = sm;
      const response = await api.post('/payments/giro/fints/sync/tan', tanPayload);
      const data = response.data;
      if (!data?.success) {
        throw new Error(data?.message || 'TAN-Verarbeitung fehlgeschlagen');
      }
      if (data.status === 'tan_required') {
        const d = data.data || {};
        setTanToken(d.tan_token || null);
        setTanChallengeText(d.challenge_text || 'TAN erforderlich');
        setTanChallengeMediaB64(d.challenge_media_base64 || null);
        setTanChallengeMediaMime(d.challenge_media_mime || null);
        setTanChallengeHhduc(d.challenge_hhduc || null);
        setTanDecoupled(!!d.decoupled);
        applyTanChallengePayload(d as Record<string, unknown>);
        setTanValue('');
        toast(d.decoupled ? 'Weitere Freigabe in der App erforderlich.' : 'Weitere TAN erforderlich.');
        return;
      }
      toast.success(
        `Giro-Umsätze importiert: ${data?.data?.imported || 0}, auto-zugeordnet: ${data?.data?.matched || 0}, Vorschläge: ${data?.data?.suggested || 0}`
      );
      setTanToken(null);
      setTanChallengeText('');
      setTanValue('');
      setTanChallengeMediaB64(null);
      setTanChallengeMediaMime(null);
      setTanChallengeHhduc(null);
      setTanSecurityFunction(null);
      setTanDecoupled(false);
      setTanMethodOptions([]);
      setSelectedTanMethod('');
      setTanProcessCode(null);
      setTanBankMediumName(null);
      queryClient.invalidateQueries('giro-transactions');
    } catch (error: any) {
      toast.error(error?.message || 'TAN-Verarbeitung fehlgeschlagen');
    } finally {
      setSyncingGiro(false);
    }
  };

  const abortGiroTanSession = () => {
    setTanToken(null);
    setTanChallengeText('');
    setTanValue('');
    setTanChallengeMediaB64(null);
    setTanChallengeMediaMime(null);
    setTanChallengeHhduc(null);
    setTanSecurityFunction(null);
    setTanDecoupled(false);
    setTanMethodOptions([]);
    setSelectedTanMethod('');
    setTanProcessCode(null);
    setTanBankMediumName(null);
    toast('FinTS-TAN abgebrochen — Sie können den Abruf später erneut starten.');
  };

  useEffect(() => {
    for (const s of pickerStudents) {
      studentPickCache.current.set(String(s.ID), s);
    }
  }, [pickerStudents]);

  const createCampaignMutation = useMutation(
    async () => {
      const title = newCampaignTitle.trim();
      if (!title) throw new Error('Bitte einen Titel eingeben.');
      const amtRaw = newCampaignAmount.replace(',', '.').trim();
      const amt = parseFloat(amtRaw);
      const hasStudents = createCampaignStudentIds.length > 0;
      if (hasStudents && (!Number.isFinite(amt) || amt <= 0)) {
        throw new Error('Mit ausgewählten Schülern ist ein Betrag größer 0 erforderlich.');
      }
      if (hasStudents && createAutoRemind && !createAutoRemindAt.trim()) {
        throw new Error('Bitte ein Datum für «Automatisch mahnen» wählen oder die Option deaktivieren.');
      }
      const campaignRes = await api.post('/payments/v2/campaigns', {
        title,
        due_at: newCampaignDueAt || null,
        status: 'active',
        description: newCampaignDescription.trim() || null,
        metadata:
          hasStudents
            ? {
                create_wizard: {
                  send_email: createSendEmail,
                  send_pdf: createSendPdf,
                  auto_remind_at: createAutoRemind ? createAutoRemindAt.trim() : null
                },
                default_amount_per_demand: Number.isFinite(amt) && amt > 0 ? amt : null,
                auto_remind_at: createAutoRemind && createAutoRemindAt.trim() ? createAutoRemindAt.trim() : null,
                auto_remind_enabled: Boolean(createAutoRemind && createAutoRemindAt.trim())
              }
            : undefined
      });
      if (!campaignRes.data?.success) {
        throw new Error(campaignRes.data?.message || 'Vorgang konnte nicht erstellt werden');
      }
      const campaignId = campaignRes.data?.data?.campaign_id as number;
      let demandCount = 0;
      const postWarnings: string[] = [];
      let createdDemandIds: number[] = [];

      if (hasStudents && campaignId) {
        const rows = createCampaignStudentIds
          .map((id) => studentPickCache.current.get(id))
          .filter(Boolean) as PickerSchuelerRow[];
        const payloadStudents = buildDemandsStudentsPayload(createCampaignStudentIds, rows);
        if (payloadStudents.length !== createCampaignStudentIds.length) {
          throw new Error(
            'Nicht alle ausgewählten Schüler sind geladen — Filter zurücksetzen oder Auswahl wiederholen.'
          );
        }
        const demandsRes = await api.post(`/payments/v2/campaigns/${campaignId}/demands`, {
          common: {
            amount: amt,
            currency: 'EUR',
            due_at: newCampaignDueAt || null,
            purpose: title.slice(0, 120)
          },
          students: payloadStudents
        });
        if (!demandsRes.data?.success) {
          throw new Error(demandsRes.data?.message || 'Forderungen konnten nicht angelegt werden');
        }
        demandCount = payloadStudents.length;
        const rawList = demandsRes.data?.data?.demands as Array<{ id: number }> | undefined;
        createdDemandIds = Array.isArray(rawList) ? rawList.map((d) => d.id).filter(Boolean) : [];

        if (createdDemandIds.length && createSendEmail) {
          try {
            const chunks = chunkNumberIds(createdDemandIds, DEMAND_BATCH_CHUNK_SIZE);
            for (let c = 0; c < chunks.length; c++) {
              const r = await api.post('/payments/v2/demands/send-email', {
                demand_ids: chunks[c],
                message: undefined,
                template: demandPdfTemplate
              });
              if (!r.data?.success) {
                postWarnings.push(
                  r.data?.message || r.data?.error || `E-Mail (Teil ${c + 1}/${chunks.length}) fehlgeschlagen`
                );
                break;
              }
            }
          } catch (e: any) {
            postWarnings.push(e?.response?.data?.message || e?.response?.data?.error || e?.message || 'E-Mail fehlgeschlagen');
          }
        }

        if (createdDemandIds.length && createSendPdf) {
          try {
            const res = await sessionFetch('/api/payments/v2/demands/print-pdf', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                demand_ids: createdDemandIds,
                template: demandPdfTemplate
              })
            });
            if (!res.ok) {
              let msg = `HTTP ${res.status}`;
              try {
                const j = await res.json();
                msg = j.message || j.error || msg;
              } catch {
                /* ignore */
              }
              postWarnings.push(msg);
            } else {
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `Forderungen_${demandPdfTemplate}_${new Date().toISOString().slice(0, 10)}.pdf`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            }
          } catch (e: any) {
            postWarnings.push(e?.message || 'PDF fehlgeschlagen');
          }
        }

        if (createdDemandIds.length && createAutoRemind && createAutoRemindAt.trim()) {
          try {
            const r = await api.post('/payments/v2/demands/schedule-reminder-emails', {
              demand_ids: createdDemandIds,
              remind_at: createAutoRemindAt.trim()
            });
            if (!r.data?.success) {
              postWarnings.push(r.data?.message || 'Geplante Mahnung fehlgeschlagen');
            } else {
              const skipped = (r.data?.data?.skipped as unknown[])?.length ?? 0;
              if (skipped > 0) {
                postWarnings.push(`${skipped} Forderung(en) ohne gültige E-Mail — Mahnung nicht eingeplant`);
              }
            }
          } catch (e: any) {
            postWarnings.push(
              e?.response?.data?.message || e?.message || 'Geplante Mahnung fehlgeschlagen'
            );
          }
        }
      }

      return { campaignRes, demandCount, postWarnings };
    },
    {
      onSuccess: (data) => {
        const resp = data.campaignRes;
        if (resp.data?.success) {
          toast.success(
            data.demandCount > 0
              ? `Vorgang angelegt; ${data.demandCount} Forderung(en) erstellt`
              : 'Vorgang erstellt'
          );
          if (data.postWarnings?.length) {
            for (const w of data.postWarnings) {
              toast.error(w);
            }
          }
          setNewCampaignTitle('');
          setNewCampaignDueAt('');
          setNewCampaignAmount('');
          setNewCampaignDescription('');
          setCreateSendEmail(true);
          setCreateSendPdf(false);
          setCreateAutoRemind(false);
          setCreateAutoRemindAt('');
          setCreateCampaignStudentIds([]);
          studentPickCache.current.clear();
          setCampaignCreateOpen(false);
          queryClient.invalidateQueries('payment-campaigns');
          queryClient.invalidateQueries(['payment-demands']);
        } else {
          toast.error(resp.data?.message || 'Vorgang konnte nicht erstellt werden');
        }
      },
      onError: (e: any) => {
        toast.error(e?.message || e?.response?.data?.message || 'Speichern fehlgeschlagen');
      }
    }
  );

  const formatDemandStatus = (s: string) => {
    const m: Record<string, string> = {
      open: 'Offen',
      partially_paid: 'Teilzahlung',
      paid: 'Bezahlt',
      overpaid: 'Überzahlt',
      cancelled: 'Storniert',
      refunded: 'Erstattet',
      paused: 'Pausiert'
    };
    return m[s] || s;
  };

  const canDeleteDemand = (d: PaymentDemand) => {
    const tot = toAmount(d.amount_total);
    const op = toAmount(d.amount_open);
    if (tot - op > 0.009) return false;
    return !['paid', 'refunded'].includes(String(d.status || ''));
  };

  const openDemandPicker = (c: PaymentCampaign) => {
    setSelectedCampaignId(c.id);
    setDemandPickerCampaign({ id: c.id, title: c.title, due_at: c.due_at ?? null });
    setDemandPickerOpen(true);
  };

  /** Alle geladenen Forderungen des gewählten Vorgangs (keine künstliche Begrenzung für Auswahl/Liste). */
  const demandRows = demands ?? [];

  const selectedCampaignMeta = useMemo(() => {
    if (!selectedCampaignId) return undefined;
    const fromMine = (campaigns || []).find((c) => c.id === selectedCampaignId);
    if (fromMine) return fromMine;
    return (schoolCampaigns || []).find((c) => c.id === selectedCampaignId);
  }, [campaigns, schoolCampaigns, selectedCampaignId]);

  const demandPickerDefaultAmountEuro = useMemo(() => {
    const raw = campaignEditAmount.replace(',', '.').trim();
    const fromForm = parseFloat(raw);
    if (Number.isFinite(fromForm) && fromForm > 0) return fromForm;
    if (!selectedCampaignMeta) return null;
    const m = parsePaymentCampaignMetadata(selectedCampaignMeta.metadata);
    const x = m.default_amount_per_demand;
    const n = typeof x === 'number' ? x : parseFloat(String(x ?? '').replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [campaignEditAmount, selectedCampaignMeta]);

  useEffect(() => {
    setCampaignEditOpen(false);
  }, [selectedCampaignId]);

  useEffect(() => {
    if (!selectedCampaignMeta) return;
    setCampaignEditTitle(selectedCampaignMeta.title || '');
    setCampaignEditDueAt(
      selectedCampaignMeta.due_at ? String(selectedCampaignMeta.due_at).slice(0, 10) : ''
    );
    setCampaignEditDescription(selectedCampaignMeta.description || '');
    setCampaignEditStatus(selectedCampaignMeta.status || 'active');
    const meta = parsePaymentCampaignMetadata(selectedCampaignMeta.metadata);
    const cw =
      meta.create_wizard && typeof meta.create_wizard === 'object'
        ? (meta.create_wizard as Record<string, unknown>)
        : {};
    const defAmt = meta.default_amount_per_demand ?? cw.default_amount_per_demand;
    setCampaignEditAmount(
      defAmt != null && defAmt !== '' ? String(defAmt).replace('.', ',') : ''
    );
    const arRaw = meta.auto_remind_at ?? cw.auto_remind_at;
    const ar = String(arRaw || '').slice(0, 10);
    setCampaignEditAutoRemindAt(ar);
    const arEn = Boolean(meta.auto_remind_enabled ?? (ar ? true : false));
    setCampaignEditAutoRemind(arEn || !!ar);
    campaignEditBaseline.current = {
      title: (selectedCampaignMeta.title || '').trim(),
      dueAt: selectedCampaignMeta.due_at ? String(selectedCampaignMeta.due_at).slice(0, 10) : '',
      description: (selectedCampaignMeta.description || '').trim(),
      status: selectedCampaignMeta.status || 'active',
      amount: defAmt != null && defAmt !== '' ? String(defAmt).replace('.', ',') : '',
      autoRemind: arEn || !!ar,
      autoRemindAt: ar,
      pdfTemplate: demandPdfTemplate
    };
  }, [selectedCampaignMeta]);

  const buildEditMetadataPayload = useCallback((): Record<string, unknown> => {
    const prev = selectedCampaignMeta
      ? parsePaymentCampaignMetadata(selectedCampaignMeta.metadata)
      : {};
    const raw = campaignEditAmount.replace(',', '.').trim();
    const parsed = parseFloat(raw);
    const defaultAmount = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    return {
      ...prev,
      default_amount_per_demand: defaultAmount,
      auto_remind_at:
        campaignEditAutoRemind && campaignEditAutoRemindAt.trim()
          ? campaignEditAutoRemindAt.trim()
          : null,
      auto_remind_enabled: campaignEditAutoRemind && !!campaignEditAutoRemindAt.trim()
    };
  }, [
    selectedCampaignMeta,
    campaignEditAmount,
    campaignEditAutoRemind,
    campaignEditAutoRemindAt
  ]);

  const hasCampaignEditChanges = useCallback((): boolean => {
    const b = campaignEditBaseline.current;
    if (!b) return true;
    const metaNow = buildEditMetadataPayload();
    const amtNow =
      metaNow.default_amount_per_demand != null ? String(metaNow.default_amount_per_demand) : '';
    const arDate = metaNow.auto_remind_at != null ? String(metaNow.auto_remind_at).slice(0, 10) : '';
    const arEn = Boolean(metaNow.auto_remind_enabled);
    const amtBefore =
      b.amount.replace(',', '.').trim() === ''
        ? ''
        : String(parseFloat(b.amount.replace(',', '.')));
    if (
      (campaignEditTitle || '').trim() !== b.title ||
      (campaignEditDueAt || '') !== b.dueAt ||
      (campaignEditDescription || '').trim() !== b.description ||
      campaignEditStatus !== b.status ||
      amtNow !== amtBefore ||
      arDate !== (b.autoRemindAt || '') ||
      arEn !== b.autoRemind ||
      demandPdfTemplate !== b.pdfTemplate
    ) {
      return true;
    }
    return false;
  }, [
    campaignEditTitle,
    campaignEditDueAt,
    campaignEditDescription,
    campaignEditStatus,
    buildEditMetadataPayload,
    campaignEditAmount,
    campaignEditAutoRemind,
    campaignEditAutoRemindAt,
    demandPdfTemplate
  ]);

  const runCampaignEditFollowup = useCallback(
    async (demandIds: number[]) => {
      if (!demandIds.length) return;
      const chunks = chunkNumberIds(demandIds, DEMAND_BATCH_CHUNK_SIZE);
      for (let c = 0; c < chunks.length; c++) {
        const r = await api.post('/payments/v2/demands/send-email', {
          demand_ids: chunks[c],
          message: undefined,
          template: demandPdfTemplate
        });
        if (!r.data?.success) {
          throw new Error(r.data?.message || r.data?.error || `E-Mail (Teil ${c + 1}) fehlgeschlagen`);
        }
      }
      const res = await sessionFetch('/api/payments/v2/demands/print-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ demand_ids: demandIds, template: demandPdfTemplate })
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          msg = j.message || j.error || msg;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Forderungen_${demandPdfTemplate}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      const meta = buildEditMetadataPayload();
      const remindAt =
        meta.auto_remind_at != null ? String(meta.auto_remind_at).slice(0, 10) : '';
      if (remindAt && meta.auto_remind_enabled) {
        const sch = await api.post('/payments/v2/demands/schedule-reminder-emails', {
          demand_ids: demandIds,
          remind_at: remindAt
        });
        if (!sch.data?.success) {
          toast.error(sch.data?.message || 'Automatische Mahnung konnte nicht eingeplant werden');
        } else {
          const skipped = (sch.data?.data?.skipped as unknown[])?.length ?? 0;
          if (skipped > 0) {
            toast(`${skipped} Forderung(en) ohne E-Mail — Mahnung teilweise nicht eingeplant`);
          }
        }
      }
    },
    [buildEditMetadataPayload, demandPdfTemplate]
  );

  const saveCampaignMutation = useMutation(
    async ({ applyToExisting }: { applyToExisting: boolean }) => {
      if (!selectedCampaignId) throw new Error('Kein Vorgang.');
      const title = campaignEditTitle.trim();
      if (!title) throw new Error('Bitte einen Titel eingeben.');
      if (campaignEditAutoRemind && !campaignEditAutoRemindAt.trim()) {
        throw new Error('Bitte ein Datum für «Automatisch mahnen» wählen oder die Option deaktivieren.');
      }
      const metadata = buildEditMetadataPayload();
      const res = await api.patch(`/payments/v2/campaigns/${selectedCampaignId}`, {
        title,
        description: campaignEditDescription.trim() || null,
        due_at: campaignEditDueAt || null,
        status: campaignEditStatus,
        metadata,
        apply_to_existing_demands: applyToExisting
      });
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Speichern fehlgeschlagen');
      }
      const data = res.data?.data as
        | { followup_demand_ids?: number[]; applied_existing?: boolean }
        | undefined;
      if (applyToExisting && data?.followup_demand_ids?.length) {
        toast.loading('E-Mail, PDF und ggf. Mahnung…', { id: 'campaign-edit-followup', duration: 120_000 });
        try {
          await runCampaignEditFollowup(data.followup_demand_ids);
        } finally {
          toast.dismiss('campaign-edit-followup');
        }
      }
      return res.data;
    },
    {
      onSuccess: (_, variables) => {
        toast.success(
          variables.applyToExisting
            ? 'Vorgang und bestehende Forderungen aktualisiert'
            : 'Vorgang gespeichert'
        );
        setCampaignScopeModalOpen(false);
        queryClient.invalidateQueries('payment-campaigns');
        queryClient.invalidateQueries(['payment-demands']);
      },
      onError: (e: any) => {
        toast.error(e?.message || e?.response?.data?.message || 'Speichern fehlgeschlagen');
      }
    }
  );

  const requestSaveCampaign = () => {
    if (!selectedCampaignId) return;
    const title = campaignEditTitle.trim();
    if (!title) {
      toast.error('Bitte einen Titel eingeben.');
      return;
    }
    if (campaignEditAutoRemind && !campaignEditAutoRemindAt.trim()) {
      toast.error('Bitte ein Datum für «Automatisch mahnen» wählen oder die Option deaktivieren.');
      return;
    }
    if (!hasCampaignEditChanges()) {
      toast('Keine Änderungen zum Speichern.');
      return;
    }
    const existingDemandCount = selectedCampaignMeta?.demand_count ?? 0;
    if (existingDemandCount === 0) {
      saveCampaignMutation.mutate({ applyToExisting: false });
      return;
    }
    setCampaignScopeModalOpen(true);
  };

  const confirmSaveCampaignOnlyNew = () => {
    saveCampaignMutation.mutate({ applyToExisting: false });
  };

  const confirmSaveCampaignAllExisting = () => {
    saveCampaignMutation.mutate({ applyToExisting: true });
  };

  const handlePauseDemand = async (id: number) => {
    setDemandBusyId(id);
    try {
      const r = await api.patch(`/payments/v2/demands/${id}`, { status: 'paused' });
      if (r.data?.success) {
        toast.success('Forderung pausiert');
        await queryClient.invalidateQueries(['payment-demands']);
      } else {
        toast.error(r.data?.message || 'Pausieren fehlgeschlagen');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Pausieren fehlgeschlagen');
    } finally {
      setDemandBusyId(null);
    }
  };

  const handleResumeDemand = async (id: number) => {
    setDemandBusyId(id);
    try {
      const r = await api.patch(`/payments/v2/demands/${id}`, { status: 'open' });
      if (r.data?.success) {
        toast.success('Forderung fortgesetzt');
        await queryClient.invalidateQueries(['payment-demands']);
      } else {
        toast.error(r.data?.message || 'Fortsetzen fehlgeschlagen');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Fortsetzen fehlgeschlagen');
    } finally {
      setDemandBusyId(null);
    }
  };

  const handleDeleteDemand = async (id: number) => {
    if (!confirm('Diese Forderung in den Papierkorb legen? Sie können sie dort wiederherstellen (Undo).')) return;
    setDemandBusyId(id);
    try {
      const r = await api.delete(`/payments/v2/demands/${id}`);
      if (r.data?.success) {
        toast.success('Forderung in den Papierkorb verschoben');
        await queryClient.invalidateQueries(['payment-demands']);
        await queryClient.invalidateQueries('payment-campaigns');
      } else {
        toast.error(r.data?.message || 'Verschieben fehlgeschlagen');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Verschieben fehlgeschlagen');
    } finally {
      setDemandBusyId(null);
    }
  };

  const handleRestoreDemand = async (id: number) => {
    setDemandBusyId(id);
    try {
      const r = await api.post(`/payments/v2/demands/${id}/restore`, {});
      if (r.data?.success) {
        toast.success('Forderung wiederhergestellt');
        await queryClient.invalidateQueries(['payment-demands']);
        await queryClient.invalidateQueries('payment-campaigns');
      } else {
        toast.error(r.data?.message || 'Wiederherstellen fehlgeschlagen');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Wiederherstellen fehlgeschlagen');
    } finally {
      setDemandBusyId(null);
    }
  };

  const handleTrashCampaign = async (campaign: PaymentCampaign) => {
    if (
      !confirm(
        `Vorgang «${campaign.title}» in den Papierkorb legen? Wiederherstellen ist möglich; bei bereits verbuchten Zahlungen verweigert das System das Verschieben.`
      )
    )
      return;
    try {
      const r = await api.delete(`/payments/v2/campaigns/${campaign.id}`);
      if (r.data?.success) {
        toast.success('Vorgang in den Papierkorb verschoben');
        await queryClient.invalidateQueries('payment-campaigns');
        await queryClient.invalidateQueries(['payment-demands']);
      } else {
        toast.error(r.data?.message || 'Verschieben fehlgeschlagen');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Verschieben fehlgeschlagen');
    }
  };

  const handleRestoreCampaign = async (id: number) => {
    try {
      const r = await api.post(`/payments/v2/campaigns/${id}/restore`, {});
      if (r.data?.success) {
        toast.success('Vorgang wiederhergestellt');
        await queryClient.invalidateQueries('payment-campaigns');
        await queryClient.invalidateQueries(['payment-demands']);
      } else {
        toast.error(r.data?.message || 'Wiederherstellen fehlgeschlagen');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Wiederherstellen fehlgeschlagen');
    }
  };

  const handleReverseManualCash = async (demand: PaymentDemand) => {
    const allocId = demand.latest_manual_cash_allocation_id;
    if (!allocId) return;
    const amt = toAmount(demand.latest_manual_cash_amount || 0).toFixed(2);
    if (
      !confirm(
        `Die letzte Barzahlung (${amt} ${demand.currency}) wirklich rückgängig machen? Der offene Betrag der Forderung wird wieder erhöht.`
      )
    ) {
      return;
    }
    setDemandBusyId(demand.id);
    try {
      const r = await api.post(`/payments/v2/allocations/${allocId}/reverse-manual-cash`, {});
      if (r.data?.success) {
        toast.success('Barzahlung storniert');
        await queryClient.invalidateQueries(['payment-demands']);
        await queryClient.invalidateQueries('payment-campaigns');
      } else {
        toast.error(r.data?.message || 'Storno fehlgeschlagen');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Storno fehlgeschlagen');
    } finally {
      setDemandBusyId(null);
    }
  };

  const handleMarkCashReceived = async (demand: PaymentDemand) => {
    const proposed = toAmount(demand.amount_open || 0).toFixed(2);
    const amountRaw = prompt('Bar erhalten - Betrag eingeben', proposed);
    if (amountRaw == null) return;
    const amount = toAmount(amountRaw);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Bitte einen gültigen Betrag eingeben.');
      return;
    }
    const note = prompt('Optionaler Hinweis/Notiz', 'Bar erhalten');
    setDemandBusyId(demand.id);
    try {
      const r = await api.post(`/payments/v2/demands/${demand.id}/manual-payment`, {
        amount,
        note: note || 'Bar erhalten'
      });
      if (r.data?.success) {
        toast.success('Barzahlung verbucht');
        await queryClient.invalidateQueries(['payment-demands']);
        await queryClient.invalidateQueries('payment-campaigns');
      } else {
        toast.error(r.data?.message || 'Barzahlung konnte nicht verbucht werden');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Barzahlung konnte nicht verbucht werden');
    } finally {
      setDemandBusyId(null);
    }
  };

  const approveSuggestionMutation = useMutation(
    (allocationId: number) => api.post(`/payments/v2/allocations/${allocationId}/approve`, {}),
    {
      onSuccess: (resp) => {
        if (resp.data?.success) {
          toast.success('Vorschlag freigegeben');
          queryClient.invalidateQueries('allocation-suggestions');
          queryClient.invalidateQueries(['payment-demands']);
          queryClient.invalidateQueries('giro-transactions');
          queryClient.invalidateQueries('bank-allocations-table');
        } else {
          toast.error(resp.data?.message || 'Freigabe fehlgeschlagen');
        }
      },
      onError: (e: any) => {
        toast.error(e?.response?.data?.message || e?.message || 'Freigabe fehlgeschlagen');
      }
    }
  );

  const rejectSuggestionMutation = useMutation(
    (allocationId: number) => api.post(`/payments/v2/allocations/${allocationId}/reject`, { notes: 'Im UI abgelehnt' }),
    {
      onSuccess: (resp) => {
        if (resp.data?.success) {
          toast.success('Vorschlag abgelehnt');
          queryClient.invalidateQueries('allocation-suggestions');
          queryClient.invalidateQueries('giro-transactions');
          queryClient.invalidateQueries('bank-allocations-table');
        } else {
          toast.error(resp.data?.message || 'Ablehnung fehlgeschlagen');
        }
      },
      onError: (e: any) => {
        toast.error(e?.response?.data?.message || e?.message || 'Ablehnung fehlgeschlagen');
      }
    }
  );

  const openBankGiroAllocationEdit = useCallback(async (tx: GiroTransaction) => {
    setBankGiroEditTx(tx);
    setBankGiroEditLoading(true);
    setBankGiroEditLines([]);
    try {
      const r = await sessionFetch(`/api/payments/v2/giro-transactions/${tx.id}/allocations`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.message || 'Laden fehlgeschlagen');
      const allocs = j.data?.allocations || [];
      const lines: BankGiroEditLine[] = allocs.map((a: Record<string, unknown>) => ({
        demand_id: Number(a.demand_id),
        amount: toAmount(a.amount).toFixed(2),
        label: `${String(a.recipient_name || '–')} · ${String(a.reference_code || '')} · ${String(
          a.campaign_title || ''
        )}`.trim()
      }));
      const fallbackAmt = toAmount(tx.amount).toFixed(2);
      setBankGiroEditLines(lines.length ? lines : [{ demand_id: 0, amount: fallbackAmt, label: '' }]);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Zuordnungen konnten nicht geladen werden');
      setBankGiroEditTx(null);
    } finally {
      setBankGiroEditLoading(false);
    }
  }, []);

  const openBankFinanceAudit = useCallback(async (tx: GiroTransaction) => {
    setBankFinanceAuditTx(tx);
    setBankFinanceAuditLoading(true);
    setBankFinanceAuditEntries([]);
    try {
      const r = await sessionFetch(`/api/payments/v2/giro-transactions/${tx.id}/finance-audit`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.message || 'Laden fehlgeschlagen');
      setBankFinanceAuditEntries(j.data?.entries || []);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Protokoll konnte nicht geladen werden');
      setBankFinanceAuditTx(null);
    } finally {
      setBankFinanceAuditLoading(false);
    }
  }, []);

  const updateBankGiroLine = (idx: number, patch: Partial<BankGiroEditLine>) => {
    setBankGiroEditLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const saveBankGiroAllocationEdit = async () => {
    if (!bankGiroEditTx) return;
    const lines = bankGiroEditLines
      .map((l) => ({ demand_id: l.demand_id, amount: toAmount(l.amount) }))
      .filter((l) => l.demand_id > 0 && l.amount > 0);
    const txAmt = Math.abs(toAmount(bankGiroEditTx.amount));
    const sum = lines.reduce((s, l) => s + l.amount, 0);
    if (sum > txAmt + 0.02) {
      toast.error('Summe der Zuordnungen übersteigt den Buchungsbetrag.');
      return;
    }
    if (lines.length === 0) {
      if (!window.confirm('Alle Zuordnungen zu dieser Buchung entfernen?')) return;
    }
    setBankGiroEditSaving(true);
    try {
      const r = await api.put(`/payments/v2/giro-transactions/${bankGiroEditTx.id}/allocations`, { lines });
      if (r.data?.success) {
        toast.success('Zuordnungen gespeichert');
        setBankGiroEditTx(null);
        await queryClient.invalidateQueries('bank-allocations-table');
        await queryClient.invalidateQueries('allocation-suggestions');
        await queryClient.invalidateQueries(['payment-demands']);
        await queryClient.invalidateQueries('payment-campaigns');
      } else {
        toast.error(r.data?.message || 'Speichern fehlgeschlagen');
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
      toast.error(msg);
    } finally {
      setBankGiroEditSaving(false);
    }
  };

  const onCampaignSort = useCallback((key: typeof campaignListSort) => {
    setCampaignListSort((prev) => {
      if (prev === key) {
        setCampaignListOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setCampaignListOrder('asc');
      return key;
    });
  }, []);

  const onDemandSort = useCallback((key: typeof demandListSort) => {
    setDemandListSort((prev) => {
      if (prev === key) {
        setDemandListOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setDemandListOrder('asc');
      return key;
    });
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="mb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">
              Zahlungen &amp; Geldkonten
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {!hasAnyPaymentCapability
                ? 'Unter Einstellungen → Berechtigungen sind für Ihre Rolle keine Zahlungsfunktionen zugewiesen.'
                : paymentPersona === 'teacher'
                  ? 'Lehreransicht: Zahlungsanlässe, Statusübersicht und Erinnerungen.'
                  : paymentPersona === 'secretariat'
                    ? 'Sekretariat: Rohdaten, Zuordnung, Sonderfälle und Klärfälle.'
                    : 'Schulleitung: Gesamtüberblick, Risiken und Steuerung.'}
            </p>
          </div>
          <Badge
            variant={backendOnline ? 'success' : 'danger'}
            className="shrink-0 gap-1.5 px-3 py-1 text-sm normal-case"
          >
            <span
              className={clsx(
                'h-2 w-2 rounded-full',
                backendOnline ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-red-500 dark:bg-red-400'
              )}
              aria-hidden
            />
            {backendOnline ? 'Backend Online' : 'Backend Offline'}
          </Badge>
        </div>
      </div>

      {!hasAnyPaymentCapability && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          Es sind keine Zahlungsbereiche freigeschaltet (Lehrer / Sekretariat / Schulleitung). Bitte prüfen Sie die
          Berechtigungsmatrix unter Einstellungen.
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-1" aria-label="Zahlungsbereiche">
          {[
            { id: 'overview' as const, name: 'Schulweite Übersicht', icon: RefreshCw },
            { id: 'transactions' as const, name: 'Eingänge (PayPal/Giro)', icon: CheckCircle },
            { id: 'campaigns' as const, name: 'meine Vorgänge', icon: FolderOpen },
            { id: 'matching' as const, name: 'Zuordnungen', icon: Link2 }
          ]
            .filter((tab) => availableTabs.includes(tab.id))
            .map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center border-b-2 py-2 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
              )}
            >
              <tab.icon className="mr-2 h-5 w-5 shrink-0" aria-hidden />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'transactions' && (
        <div className="space-y-6">
          <ListToolbar
            title="Zahlungseingänge"
            description="Giro (FinTS) und PayPal — Synchronisation und letzte Umsätze."
            trailing={
              backendOnline ? (
                <>
                  <Button type="button" variant="primary" size="sm" onClick={startGiroSync} disabled={syncingGiro}>
                    {syncingGiro ? 'FinTS läuft...' : 'Giro-Umsätze abrufen'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={async () => {
                      try {
                        const res = await api.post('/payments/reconcile', {});
                        const data = res.data;
                        if (data.success) {
                          toast.success(`PayPal-Abgleich abgeschlossen. Aktualisiert: ${data.data?.updated || 0}`);
                          queryClient.invalidateQueries('paypal-transactions');
                        } else {
                          toast.error(data.message || 'Abgleich fehlgeschlagen');
                        }
                      } catch (e: any) {
                        toast.error(`Abgleich-Fehler: ${e.message || e}`);
                      }
                    }}
                  >
                    PayPal synchronisieren
                  </Button>
                </>
              ) : null
            }
          />

          <section className="card overflow-hidden p-0">
            <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Giro-Umsätze (FinTS)</h2>
            </div>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {loadingGiroTransactions ? (
                <li className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400">Lädt...</li>
              ) : (giroTransactions || []).length === 0 ? (
                <li className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Keine Giro-Umsätze vorhanden
                </li>
              ) : (
                (giroTransactions || []).slice(0, 20).map((tx) => (
                  <li key={tx.external_id} className="px-4 py-4 sm:px-6">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {tx.counterpart_name || 'Unbekannter Zahler'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{tx.purpose || '-'}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {tx.booking_date ? new Date(tx.booking_date).toLocaleDateString('de-DE') : '-'}
                        </p>
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="text-lg font-medium text-emerald-700 dark:text-emerald-400">
                          +{toAmount(tx.amount).toFixed(2)} {tx.currency}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {tx.allocation_state
                            ? `Status: ${tx.allocation_state}`
                            : tx.match_method
                              ? `Gematcht (${tx.match_method})`
                              : 'Nicht zugeordnet'}
                        </p>
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </section>

          <section className="card overflow-hidden p-0">
            <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">PayPal-Transaktionen</h2>
            </div>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {loadingTransactions ? (
                <li className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400">Lädt...</li>
              ) : transactions?.length === 0 ? (
                <li className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Keine Transaktionen vorhanden
                </li>
              ) : (
                transactions?.map((transaction) => (
                  <li key={transaction.id} className="px-4 py-4 sm:px-6">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500 dark:text-emerald-400" aria-hidden />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {transaction.payer.name.given_name} {transaction.payer.name.surname}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{transaction.payer.email_address}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {new Date(transaction.create_time).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="text-lg font-medium text-emerald-600 dark:text-emerald-400">
                          +{transaction.amount.value} {transaction.amount.currency_code}
                        </p>
                        <Badge variant="neutral" className="mt-1">
                          {transaction.status}
                        </Badge>
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </section>
        </div>
      )}

      {campaignUiActive && (
        <div className="space-y-6">
          {activeTab === 'overview' && paymentPersona === 'leadership' && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                {
                  n: String(leadershipOverview?.totals?.demand_count || 0),
                  l: 'Forderungen gesamt',
                  numClass: 'text-gray-900 dark:text-gray-50'
                },
                {
                  n: `${toAmount(leadershipOverview?.totals?.amount_open || 0).toFixed(2)}`,
                  l: 'Offener Betrag (EUR)',
                  numClass: 'text-amber-700 dark:text-amber-300'
                },
                {
                  n: `${toAmount(leadershipOverview?.totals?.overdue_open_amount || 0).toFixed(2)}`,
                  l: 'Überfällig (EUR)',
                  numClass: 'text-red-700 dark:text-red-300'
                },
                {
                  n: String(leadershipOverview?.totals?.paid_count || 0),
                  l: 'Bezahlt (Anzahl)',
                  numClass: 'text-emerald-700 dark:text-emerald-300'
                }
              ].map((k) => (
                <Tile key={k.l} variant="compact">
                  <TileContent className="py-4 text-center">
                    <p className={clsx('text-2xl font-semibold tabular-nums', k.numClass)}>{k.n}</p>
                    <TileDescription className="mt-0.5">{k.l}</TileDescription>
                  </TileContent>
                </Tile>
              ))}
            </div>
          )}
          {!selectedCampaignId ? (
            demandTrashOnly ? (
              <section className="card p-6">
                <ListToolbar
                  title="Papierkorb – Forderungen"
                  description="Einzelforderungen, die Sie in den Papierkorb gelegt haben. Vorgänge finden Sie unter «Papierkorb – Vorgänge»."
                  trailing={
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setDemandTrashOnly(false)}
                    >
                      Zurück zur Vorgangsliste
                    </Button>
                  }
                />
                {loadingDemands ? (
                  <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Lädt…</p>
                ) : demandRows.length === 0 ? (
                  <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    Keine Forderungen im Papierkorb.
                  </p>
                ) : (
                  <TableScrollArea>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeaderCell>Empfänger</TableHeaderCell>
                          <TableHeaderCell>Vorgang</TableHeaderCell>
                          <TableHeaderCell>Referenz</TableHeaderCell>
                          <TableHeaderCell className="text-right">Offen</TableHeaderCell>
                          <TableHeaderCell>Aktion</TableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {demandRows.map((demand) => {
                          const busy = demandBusyId === demand.id;
                          return (
                            <TableRow key={demand.id}>
                              <TableCell className="text-sm">{demand.recipient_name || '–'}</TableCell>
                              <TableCell className="max-w-[14rem] whitespace-normal text-sm">
                                {demand.campaign_title || '–'}
                              </TableCell>
                              <TableCell className="font-mono text-xs">{demand.reference_code}</TableCell>
                              <TableCell align="right" className="tabular-nums text-sm">
                                {toAmount(demand.amount_open).toFixed(2)} {demand.currency}
                              </TableCell>
                              <TableCell>
                                <Button
                                  type="button"
                                  variant="primary"
                                  size="sm"
                                  disabled={busy || !backendOnline}
                                  onClick={() => void handleRestoreDemand(demand.id)}
                                >
                                  <Undo2 className="mr-1 h-3.5 w-3.5" aria-hidden />
                                  Wiederherstellen
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableScrollArea>
                )}
              </section>
            ) : !campaignCreateOpen ? (
          <section className="card p-6">
            <ListToolbar
              title={
                campaignTrashOnly
                  ? 'Papierkorb – Vorgänge'
                  : activeTab === 'overview'
                    ? 'Schulweite Übersicht'
                    : 'meine Vorgänge'
              }
              description={
                campaignTrashOnly
                  ? 'Wiederherstellen stellt den Vorgang und alle zugehörigen Forderungen wieder sichtbar her (ohne neue Zahlungsdaten).'
                  : activeTab === 'overview'
                    ? 'Alle Zahlungsvorgänge der Schule — gleiche Darstellung wie unter «meine Vorgänge».'
                    : 'Nur Ihre eigenen Zahlungsvorgänge. Neuen Vorgang anlegen — Forderungen je Zeile öffnen.'
              }
              trailing={
                <>
                  {activeTab === 'campaigns' && !campaignTrashOnly ? (
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setCampaignTrashOnly(false);
                        setDemandTrashOnly(false);
                        setCampaignCreateOpen(true);
                      }}
                    >
                      <Plus className="mr-1 h-4 w-4" aria-hidden />
                      Neuen Vorgang anlegen
                    </Button>
                  ) : null}
                  <label htmlFor="pm-campaign-search" className="sr-only">
                    Vorgänge durchsuchen
                  </label>
                  <input
                    id="pm-campaign-search"
                    type="search"
                    value={campaignListSearch}
                    onChange={(e) => setCampaignListSearch(e.target.value)}
                    placeholder="Titel, Beschreibung…"
                    className="input-field max-w-xs min-h-[2.25rem] py-2 text-sm"
                    autoComplete="off"
                  />
                  <label htmlFor="pm-campaign-status" className="sr-only">
                    Statusfilter Vorgänge
                  </label>
                  <select
                    id="pm-campaign-status"
                    value={campaignListStatus}
                    onChange={(e) => setCampaignListStatus(e.target.value)}
                    className="input-field max-w-[10rem] min-h-[2.25rem] py-2 text-sm"
                  >
                    <option value="">Alle Status</option>
                    <option value="draft">Entwurf</option>
                    <option value="active">Aktiv</option>
                    <option value="closed">Abgeschlossen</option>
                    <option value="cancelled">Abgebrochen</option>
                  </select>
                  <label className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary-600 dark:border-gray-600"
                      checked={campaignTrashOnly}
                      disabled={!!campaignCreateOpen}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setCampaignTrashOnly(v);
                        if (v) setDemandTrashOnly(false);
                      }}
                    />
                    Papierkorb Vorgänge
                  </label>
                  <label className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary-600 dark:border-gray-600"
                      checked={demandTrashOnly}
                      disabled={!!campaignCreateOpen}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setDemandTrashOnly(v);
                        if (v) {
                          setCampaignTrashOnly(false);
                          setCampaignCreateOpen(false);
                        }
                      }}
                    />
                    Papierkorb Forderungen
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => queryClient.invalidateQueries('payment-campaigns')}
                  >
                    Aktualisieren
                  </Button>
                </>
              }
            />
            {loadingDisplayedCampaigns ? (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Lädt Vorgänge…</p>
            ) : (
              <TableScrollArea>
                <Table>
                  <TableHead>
                    <TableRow>
                      <SortableTh
                        sortKey="title"
                        activeKey={campaignListSort}
                        direction={campaignListOrder}
                        onSort={onCampaignSort}
                        className="whitespace-normal"
                      >
                        Vorgang
                      </SortableTh>
                      <SortableTh
                        sortKey="status"
                        activeKey={campaignListSort}
                        direction={campaignListOrder}
                        onSort={onCampaignSort}
                      >
                        Status
                      </SortableTh>
                      <SortableTh
                        sortKey="created_at"
                        activeKey={campaignListSort}
                        direction={campaignListOrder}
                        onSort={onCampaignSort}
                      >
                        Erstellt
                      </SortableTh>
                      <SortableTh
                        sortKey="due_at"
                        activeKey={campaignListSort}
                        direction={campaignListOrder}
                        onSort={onCampaignSort}
                      >
                        Fällig
                      </SortableTh>
                      <SortableTh
                        sortKey="open_amount"
                        activeKey={campaignListSort}
                        direction={campaignListOrder}
                        onSort={onCampaignSort}
                        className="text-right"
                      >
                        Offen (EUR)
                      </SortableTh>
                      <SortableTh
                        sortKey="demand_count"
                        activeKey={campaignListSort}
                        direction={campaignListOrder}
                        onSort={onCampaignSort}
                        className="text-right"
                      >
                        #
                      </SortableTh>
                      <SortableTh
                        sortKey="total_amount"
                        activeKey={campaignListSort}
                        direction={campaignListOrder}
                        onSort={onCampaignSort}
                        className="text-right"
                      >
                        Summe (EUR)
                      </SortableTh>
                      <TableHeaderCell className="whitespace-normal">Aktionen</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedCampaigns.length === 0 ? (
                      <EmptyTableRow
                        colSpan={8}
                        title={campaignTrashOnly ? 'Papierkorb leer' : 'Keine Vorgänge'}
                        description={
                          campaignTrashOnly
                            ? 'Keine Vorgänge im Papierkorb.'
                            : activeTab === 'overview'
                              ? 'Keine Vorgänge — Suche oder Statusfilter anpassen.'
                              : 'Legen Sie über «Neuen Vorgang anlegen» einen Vorgang an oder passen Sie Suche/Filter an.'
                        }
                      />
                    ) : (
                      displayedCampaigns.map((campaign) => (
                        <TableRow key={campaign.id}>
                          <TableCell className="max-w-[14rem] whitespace-normal">
                            <p className="font-medium text-gray-900 dark:text-gray-100">{campaign.title}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              von {campaign.created_by_username || '–'}
                            </p>
                          </TableCell>
                          <TableCell>
                            <Badge variant={campaignStatusBadgeVariant(campaign.status)}>
                              {CAMPAIGN_STATUS_LABEL[campaign.status] || campaign.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                            {formatPaymentListDateTime(campaign.created_at)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                            {campaign.due_at ? formatPaymentListDateTime(campaign.due_at) : '–'}
                          </TableCell>
                          <TableCell align="right" className="tabular-nums text-sm">
                            {toAmount(campaign.open_amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right" className="tabular-nums text-sm">
                            {campaign.demand_count || 0}
                          </TableCell>
                          <TableCell align="right" className="tabular-nums text-sm">
                            {toAmount(campaign.total_amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="whitespace-normal">
                            {campaignTrashOnly ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="primary"
                                onClick={() => void handleRestoreCampaign(campaign.id)}
                              >
                                <Undo2 className="mr-1 h-3.5 w-3.5" aria-hidden />
                                Wiederherstellen
                              </Button>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="primary"
                                  onClick={() => setSelectedCampaignId(campaign.id)}
                                >
                                  Detail
                                </Button>
                                {(activeTab === 'campaigns' ||
                                  paymentPersona === 'leadership' ||
                                  paymentPersona === 'secretariat') && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="text-gray-700 dark:text-gray-200"
                                    onClick={() => void handleTrashCampaign(campaign)}
                                  >
                                    <Trash2 className="mr-1 h-3.5 w-3.5" aria-hidden />
                                    Papierkorb
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableScrollArea>
            )}
          </section>
            ) : activeTab === 'campaigns' ? (
          <section className="card p-6">
            <div className="mb-6 border-b border-gray-200 pb-4 dark:border-gray-700">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="-ml-2 mb-2"
                onClick={() => setCampaignCreateOpen(false)}
              >
                <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
                Zurück zur Vorgangsliste
              </Button>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Neuen Vorgang anlegen</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Titel, Betrag je Forderung und optional Schüler — der Freitext erscheint in E-Mail und PDF. Ohne
                Schülerauswahl wird nur der Vorgang ohne Forderungen erstellt. Mit Schülern können Sie
                E-Mail und/oder PDF direkt auslösen und optional eine automatische Mahnung terminieren.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                Titel
                <input
                  value={newCampaignTitle}
                  onChange={(e) => setNewCampaignTitle(e.target.value)}
                  placeholder="z. B. Parisfahrt 2026"
                  className="input-field min-h-[2.25rem] py-2 text-sm"
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                Fällig bis (optional)
                <input
                  type="date"
                  value={newCampaignDueAt}
                  onChange={(e) => setNewCampaignDueAt(e.target.value)}
                  className="input-field min-h-[2.25rem] py-2 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                Betrag je Forderung (EUR)
                <input
                  type="text"
                  inputMode="decimal"
                  value={newCampaignAmount}
                  onChange={(e) => setNewCampaignAmount(e.target.value)}
                  placeholder={createCampaignStudentIds.length > 0 ? 'z. B. 25,00' : 'bei Schülerauswahl'}
                  className="input-field min-h-[2.25rem] py-2 text-sm"
                  autoComplete="off"
                />
              </label>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="primary"
                  className="w-full lg:w-auto"
                  onClick={() => createCampaignMutation.mutate()}
                  disabled={!newCampaignTitle.trim() || createCampaignMutation.isLoading}
                >
                  Vorgang anlegen
                </Button>
              </div>
            </div>
            <label className="mt-4 flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
              Hinweis für E-Mail und PDF (optional)
              <textarea
                value={newCampaignDescription}
                onChange={(e) => setNewCampaignDescription(e.target.value)}
                placeholder="z. B. Bitte überweisen Sie bis zum angegebenen Termin …"
                rows={3}
                className="input-field text-sm"
              />
            </label>

            <PaymentStudentMultiSelect
              enabled={
                activeTab === 'campaigns' &&
                campaignCreateOpen &&
                !selectedCampaignId &&
                hasAnyPaymentCapability &&
                backendOnline
              }
              selectedIds={createCampaignStudentIds}
              onSelectedIdsChange={setCreateCampaignStudentIds}
              onStudentsChange={setPickerStudents}
            />

            <div
              className={clsx(
                'mt-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700',
                createCampaignStudentIds.length === 0 && 'opacity-60'
              )}
            >
              <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                Nach dem Anlegen (bei ausgewählten Schülern)
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                PDF lädt eine kombinierte Datei herunter. Automatische Mahnung versendet am gewählten Termin eine
                Zahlungserinnerung per E-Mail (nur bei gültiger Empfänger-Adresse).
              </p>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={createSendEmail}
                    onChange={(e) => setCreateSendEmail(e.target.checked)}
                    disabled={createCampaignStudentIds.length === 0}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                  />
                  E-Mail versenden
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={createSendPdf}
                    onChange={(e) => setCreateSendPdf(e.target.checked)}
                    disabled={createCampaignStudentIds.length === 0}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                  />
                  PDF erzeugen (Download)
                </label>
              </div>
              <label className="mt-3 flex max-w-md flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                Vorlage (PDF &amp; E-Mail)
                <select
                  value={demandPdfTemplate}
                  onChange={(e) =>
                    setDemandPdfTemplate(e.target.value as 'professional' | 'playful' | 'simple')
                  }
                  disabled={createCampaignStudentIds.length === 0}
                  className="input-field min-h-[2.25rem] py-2 text-sm"
                >
                  <option value="professional">Seriös (Karte + QR)</option>
                  <option value="playful">Freundlich (Pastell + Rakete)</option>
                  <option value="simple">Kompakt (Liste)</option>
                </select>
              </label>
              <div className="mt-4 flex flex-wrap items-end gap-4">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={createAutoRemind}
                    onChange={(e) => {
                      setCreateAutoRemind(e.target.checked);
                      if (!e.target.checked) setCreateAutoRemindAt('');
                    }}
                    disabled={createCampaignStudentIds.length === 0}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                  />
                  Automatisch mahnen am
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                  <span className={createAutoRemind ? '' : 'sr-only'}>Datum</span>
                  <input
                    id="pm-create-auto-remind-date"
                    type="date"
                    value={createAutoRemindAt}
                    onChange={(e) => setCreateAutoRemindAt(e.target.value)}
                    disabled={createCampaignStudentIds.length === 0 || !createAutoRemind}
                    className="input-field min-h-[2.25rem] py-2 text-sm"
                  />
                </label>
              </div>
              {createCampaignStudentIds.length === 0 ? (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                  Wählen Sie oben Schüler aus, um Versand und Mahnung zu aktivieren.
                </p>
              ) : null}
            </div>
          </section>
            ) : null
          ) : (
          <section className="card p-6">
            <div className="mb-6 space-y-4 border-b border-gray-200 pb-4 dark:border-gray-700">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="-ml-2 mb-2"
                    onClick={() => setSelectedCampaignId(null)}
                  >
                    <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
                    Zurück zur Vorgangsliste
                  </Button>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                    Forderungen: {selectedCampaignMeta?.title ?? `Vorgang #${selectedCampaignId}`}
                  </h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Nur dieser Vorgang. Sortierung über die Spaltenköpfe. Versand und Vorlage legen Sie beim Anlegen
                    fest; hier ändern Sie Stammdaten und ergänzen Schüler.
                  </p>
                </div>
                {backendOnline && selectedCampaignId != null ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => setCampaignEditOpen((o) => !o)}
                    >
                      <Pencil className="mr-1 h-4 w-4" aria-hidden />
                      {campaignEditOpen ? 'Bearbeiten schließen' : 'Vorgang bearbeiten'}
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      className="shrink-0"
                      onClick={() =>
                        openDemandPicker(
                          selectedCampaignMeta
                            ? { ...selectedCampaignMeta }
                            : {
                                id: selectedCampaignId!,
                                title: `Vorgang #${selectedCampaignId}`,
                                status: 'active'
                              }
                        )
                      }
                    >
                      <Plus className="mr-1 h-4 w-4" aria-hidden />
                      Weitere Schüler hinzufügen
                    </Button>
                  </div>
                ) : null}
              </div>
              {campaignEditOpen && (
                <div className="rounded-lg border border-gray-200 bg-gray-50/90 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Vorgang bearbeiten</h3>
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                    Standardbetrag und Mahnung werden im Vorgang gespeichert. Beim Anwenden auf alle bestehenden
                    Forderungen: Betrag nur bei vollständig offenen Posten, Fälligkeit und Verwendungszweck wo möglich,
                    danach E-Mail und PDF erneut — Vorlage unten wählen.
                  </p>
                  <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                      Titel
                      <input
                        value={campaignEditTitle}
                        onChange={(e) => setCampaignEditTitle(e.target.value)}
                        className="input-field min-h-[2.25rem] py-2 text-sm"
                        autoComplete="off"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                      Fällig bis (optional)
                      <input
                        type="date"
                        value={campaignEditDueAt}
                        onChange={(e) => setCampaignEditDueAt(e.target.value)}
                        className="input-field min-h-[2.25rem] py-2 text-sm"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                      Betrag je Forderung (EUR, Standard)
                      <input
                        type="text"
                        inputMode="decimal"
                        value={campaignEditAmount}
                        onChange={(e) => setCampaignEditAmount(e.target.value)}
                        placeholder="z. B. 25,00"
                        className="input-field min-h-[2.25rem] py-2 text-sm"
                        autoComplete="off"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                      Vorlage für E-Mail &amp; PDF (bei Aktualisierung)
                      <select
                        value={demandPdfTemplate}
                        onChange={(e) =>
                          setDemandPdfTemplate(e.target.value as 'professional' | 'playful' | 'simple')
                        }
                        className="input-field min-h-[2.25rem] py-2 text-sm"
                      >
                        <option value="professional">Seriös (Karte + QR)</option>
                        <option value="playful">Freundlich (Pastell + Rakete)</option>
                        <option value="simple">Kompakt (Liste)</option>
                      </select>
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300 md:col-span-2">
                      Hinweis für E-Mail und PDF (optional)
                      <textarea
                        value={campaignEditDescription}
                        onChange={(e) => setCampaignEditDescription(e.target.value)}
                        rows={3}
                        className="input-field text-sm"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                      Status
                      <select
                        value={campaignEditStatus}
                        onChange={(e) => setCampaignEditStatus(e.target.value)}
                        className="input-field min-h-[2.25rem] py-2 text-sm"
                      >
                        <option value="draft">Entwurf</option>
                        <option value="active">Aktiv</option>
                        <option value="closed">Abgeschlossen</option>
                        <option value="cancelled">Abgebrochen</option>
                      </select>
                    </label>
                  </div>
                  <div className="mt-4 flex flex-wrap items-end gap-4 rounded-lg border border-dashed border-gray-200 p-3 dark:border-gray-600">
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={campaignEditAutoRemind}
                        onChange={(e) => {
                          setCampaignEditAutoRemind(e.target.checked);
                          if (!e.target.checked) setCampaignEditAutoRemindAt('');
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                      />
                      Automatisch mahnen am
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                      <span className={campaignEditAutoRemind ? '' : 'sr-only'}>Datum</span>
                      <input
                        type="date"
                        value={campaignEditAutoRemindAt}
                        onChange={(e) => setCampaignEditAutoRemindAt(e.target.value)}
                        disabled={!campaignEditAutoRemind}
                        className="input-field min-h-[2.25rem] py-2 text-sm"
                      />
                    </label>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => requestSaveCampaign()}
                      disabled={saveCampaignMutation.isLoading || !campaignEditTitle.trim()}
                    >
                      Speichern
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setCampaignEditOpen(false)}
                    >
                      Schließen
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <ListToolbar
              title="Liste &amp; Filter"
              description={
                !loadingDemands
                  ? `${demandRows.length} Einträge`
                  : 'Liste wird geladen…'
              }
              trailing={
                <>
                  <label htmlFor="pm-demand-search" className="sr-only">
                    Forderungen durchsuchen
                  </label>
                  <input
                    id="pm-demand-search"
                    type="search"
                    value={demandListSearch}
                    onChange={(e) => setDemandListSearch(e.target.value)}
                    placeholder="Name, Referenz, Vorgang…"
                    className="input-field max-w-xs min-h-[2.25rem] py-2 text-sm"
                    autoComplete="off"
                  />
                  <label htmlFor="pm-demand-status" className="sr-only">
                    Statusfilter Forderungen
                  </label>
                  <select
                    id="pm-demand-status"
                    value={demandListStatus}
                    onChange={(e) => setDemandListStatus(e.target.value)}
                    className="input-field max-w-[11rem] min-h-[2.25rem] py-2 text-sm"
                  >
                    <option value="">Alle Status</option>
                    <option value="open">Offen</option>
                    <option value="partially_paid">Teilzahlung</option>
                    <option value="paid">Bezahlt</option>
                    <option value="overpaid">Überzahlt</option>
                    <option value="paused">Pausiert</option>
                    <option value="cancelled">Storniert</option>
                    <option value="refunded">Erstattet</option>
                  </select>
                </>
              }
            />
            {loadingDemands ? (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Lädt Forderungen…</p>
            ) : demandRows.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                Keine Forderungen in diesem Vorgang — passen Sie Suche oder Statusfilter an oder legen Sie Forderungen
                an.
              </p>
            ) : (
              <TableScrollArea>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <SortableTh
                          sortKey="recipient_name"
                          activeKey={demandListSort}
                          direction={demandListOrder}
                          onSort={onDemandSort}
                          className="whitespace-normal"
                        >
                          Empfänger
                        </SortableTh>
                        <SortableTh
                          sortKey="created_at"
                          activeKey={demandListSort}
                          direction={demandListOrder}
                          onSort={onDemandSort}
                        >
                          Erstellt
                        </SortableTh>
                        <SortableTh
                          sortKey="campaign_title"
                          activeKey={demandListSort}
                          direction={demandListOrder}
                          onSort={onDemandSort}
                          className="whitespace-normal"
                        >
                          Vorgang
                        </SortableTh>
                        <SortableTh
                          sortKey="reference_code"
                          activeKey={demandListSort}
                          direction={demandListOrder}
                          onSort={onDemandSort}
                        >
                          Referenz
                        </SortableTh>
                        <SortableTh
                          sortKey="due_at"
                          activeKey={demandListSort}
                          direction={demandListOrder}
                          onSort={onDemandSort}
                        >
                          Fällig
                        </SortableTh>
                        <SortableTh
                          sortKey="amount_open"
                          activeKey={demandListSort}
                          direction={demandListOrder}
                          onSort={onDemandSort}
                          className="text-right"
                        >
                          Offen
                        </SortableTh>
                        <SortableTh
                          sortKey="amount_total"
                          activeKey={demandListSort}
                          direction={demandListOrder}
                          onSort={onDemandSort}
                          className="text-right"
                        >
                          Gesamt
                        </SortableTh>
                        <SortableTh
                          sortKey="status"
                          activeKey={demandListSort}
                          direction={demandListOrder}
                          onSort={onDemandSort}
                        >
                          Status
                        </SortableTh>
                        <TableHeaderCell className="whitespace-normal">Aktionen</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {demandRows.map((demand) => {
                        const st = String(demand.status || '');
                        const busy = demandBusyId === demand.id;
                        const canPause = ['open', 'partially_paid', 'overpaid'].includes(st);
                        const canResume = st === 'paused';
                        const showDelete = canDeleteDemand(demand) && backendOnline;
                        const canRecordCash =
                          toAmount(demand.amount_open) > 0.001 &&
                          !['paused', 'paid', 'cancelled', 'refunded'].includes(st);
                        return (
                          <TableRow key={demand.id}>
                            <TableCell className="max-w-[14rem] whitespace-normal align-top">
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {demand.recipient_name || 'Unbekannt'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Von {demand.created_by_username || '–'}
                              </p>
                              {demand.last_received_at ? (
                                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                  Letzter Eingang: {toAmount(demand.last_received_amount || 0).toFixed(2)}{' '}
                                  {demand.currency} am {new Date(demand.last_received_at).toLocaleDateString('de-DE')}
                                </p>
                              ) : null}
                              {Number(demand.has_unclear_flag || 0) > 0 ? (
                                <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
                                  Unklare Zuordnung
                                </p>
                              ) : null}
                              {Number(demand.manual_cash_entry_count || 0) > 1 ? (
                                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                  {Number(demand.manual_cash_entry_count)} Barbuchungen
                                </p>
                              ) : null}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              {formatPaymentListDateTime(demand.created_at)}
                            </TableCell>
                            <TableCell className="max-w-[12rem] whitespace-normal text-sm text-gray-700 dark:text-gray-300">
                              {demand.campaign_title || '–'}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-gray-800 dark:text-gray-200">
                              {demand.reference_code}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              {demand.due_at ? formatPaymentListDateTime(demand.due_at) : '–'}
                            </TableCell>
                            <TableCell align="right" className="tabular-nums text-sm">
                              {toAmount(demand.amount_open).toFixed(2)} {demand.currency}
                            </TableCell>
                            <TableCell align="right" className="tabular-nums text-sm">
                              {toAmount(demand.amount_total).toFixed(2)} {demand.currency}
                            </TableCell>
                            <TableCell>
                              <Badge variant={demandStatusBadgeVariant(st)}>{formatDemandStatus(st)}</Badge>
                            </TableCell>
                            <TableCell className="min-w-[12rem] whitespace-normal align-top">
                              {backendOnline ? (
                                <div className="flex max-w-xl flex-wrap gap-1">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="xs"
                                    disabled={busy || !canRecordCash}
                                    onClick={() => void handleMarkCashReceived(demand)}
                                    title={
                                      !canRecordCash ? 'Kein offener Betrag oder Forderung nicht buchbar' : undefined
                                    }
                                  >
                                    Bar
                                  </Button>
                                  {demand.latest_manual_cash_allocation_id ? (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="xs"
                                      disabled={busy}
                                      onClick={() => void handleReverseManualCash(demand)}
                                      title="Letzte Barbuchung stornieren"
                                    >
                                      <Undo2 className="mr-0.5 h-3.5 w-3.5" aria-hidden />
                                      Storno
                                    </Button>
                                  ) : null}
                                  {canPause && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="xs"
                                      disabled={busy}
                                      onClick={() => void handlePauseDemand(demand.id)}
                                    >
                                      <Pause className="mr-0.5 h-3.5 w-3.5" aria-hidden />
                                      Pausieren
                                    </Button>
                                  )}
                                  {!isTeacherPersona && canResume && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="xs"
                                      disabled={busy}
                                      onClick={() => void handleResumeDemand(demand.id)}
                                    >
                                      <Play className="mr-0.5 h-3.5 w-3.5" aria-hidden />
                                      Fortsetzen
                                    </Button>
                                  )}
                                  {!isTeacherPersona && showDelete && (
                                    <Button
                                      type="button"
                                      variant="danger"
                                      size="xs"
                                      disabled={busy}
                                      onClick={() => void handleDeleteDemand(demand.id)}
                                    >
                                      <Trash2 className="mr-0.5 h-3.5 w-3.5" aria-hidden />
                                      Löschen
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400 dark:text-gray-500">Offline</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableScrollArea>
            )}
          </section>
          )}
        </div>
      )}

      {activeTab === 'matching' && (
        <div className="space-y-6">
          <section className="card p-6">
            <ListToolbar
              title="Kontoauszug & Zuordnungen"
              description="Nur Zahlungseingänge (Gutschriften). Nach jedem FinTS-Abruf unter «Eingänge» werden neue Umsätze importiert und automatisch den Forderungen zugeordnet (soweit möglich). Verknüpfung zu Vorgang, Forderung (Empfänger) und Ersteller; Filter unten."
              trailing={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => queryClient.invalidateQueries('bank-allocations-table')}
                >
                  Aktualisieren
                </Button>
              }
            />
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <label className="flex min-w-[11rem] flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                Schnellfilter
                <select
                  value={bankPreset}
                  onChange={(e) => setBankPreset(e.target.value)}
                  className="input-field min-h-[2.25rem] py-2 text-sm"
                >
                  <option value="all">Alle Posten</option>
                  <option value="lr_likely">Mutmaßlich Lernrakete (Ref. LR …)</option>
                  <option value="matching_open">Zuordnung offen / Vorschlag</option>
                  <option value="linked">Mit Forderung verbunden</option>
                  <option value="unlinked">Noch keiner Forderung zugeordnet</option>
                  <option value="partial">Nur teilweise verbucht</option>
                  <option value="ignored">Ignoriert / nicht zuordenbar</option>
                </select>
              </label>
              {(paymentPersona === 'leadership' || paymentPersona === 'secretariat') && (
                <label className="flex min-w-[12rem] flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                  Vorgang
                  <select
                    value={bankCampaignId}
                    onChange={(e) => setBankCampaignId(e.target.value)}
                    className="input-field min-h-[2.25rem] py-2 text-sm"
                  >
                    <option value="">Alle Vorgänge</option>
                    {(bankFilterCampaigns || []).map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="flex min-w-[10rem] flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                Ersteller (Nutzername)
                <input
                  type="search"
                  value={bankCreator}
                  onChange={(e) => setBankCreator(e.target.value)}
                  placeholder="z. B. max.mustermann"
                  className="input-field min-h-[2.25rem] py-2 text-sm"
                  autoComplete="off"
                />
              </label>
              <label className="flex min-w-[10rem] flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                Schüler / Empfänger
                <input
                  type="search"
                  value={bankStudentQ}
                  onChange={(e) => setBankStudentQ(e.target.value)}
                  placeholder="Name oder Referenzcode"
                  className="input-field min-h-[2.25rem] py-2 text-sm"
                  autoComplete="off"
                />
              </label>
              <label className="flex min-w-[12rem] flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                Freitext (Zahler, Zweck, IBAN…)
                <input
                  type="search"
                  value={bankTextQ}
                  onChange={(e) => setBankTextQ(e.target.value)}
                  placeholder="Suche in Buchung"
                  className="input-field min-h-[2.25rem] py-2 text-sm"
                  autoComplete="off"
                />
              </label>
              <label className="flex min-w-[9rem] flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                Bank-Status
                <select
                  value={bankState}
                  onChange={(e) => setBankState(e.target.value)}
                  className="input-field min-h-[2.25rem] py-2 text-sm"
                >
                  <option value="">Alle Stati</option>
                  <option value="unprocessed">Offen</option>
                  <option value="suggested">Vorschlag</option>
                  <option value="partially_allocated">Teilweise</option>
                  <option value="fully_allocated">Verbucht</option>
                  <option value="ignored">Ignoriert</option>
                </select>
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mb-0.5"
                onClick={() => {
                  setBankPreset('all');
                  setBankCampaignId('');
                  setBankCreator('');
                  setBankStudentQ('');
                  setBankTextQ('');
                  setBankState('');
                  setBankPage(1);
                }}
              >
                Filter zurücksetzen
              </Button>
            </div>
            {loadingBankAllocations ? (
              <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Lädt Kontoauszug…</p>
            ) : (
              <>
                <TableScrollArea>
                  <Table className="min-w-[1520px]">
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell className="whitespace-nowrap">Buchung</TableHeaderCell>
                        <TableHeaderCell className="whitespace-nowrap text-right">Betrag</TableHeaderCell>
                        <TableHeaderCell className="min-w-[12rem] max-w-[18rem]">Zahler</TableHeaderCell>
                        <TableHeaderCell className="min-w-[26rem] max-w-[36rem] xl:min-w-[28rem]">
                          Verwendungszweck
                        </TableHeaderCell>
                        <TableHeaderCell className="min-w-[8.5rem] whitespace-nowrap">Status</TableHeaderCell>
                        <TableHeaderCell
                          className="min-w-[16rem] max-w-[22rem]"
                          title="Automatisch (Referenz, Name …), Freigabe eines Vorschlags oder manuelle Zuordnung"
                        >
                          Herkunft Zuordnung
                        </TableHeaderCell>
                        <TableHeaderCell className="min-w-[11rem] max-w-[15rem]">Vorgang</TableHeaderCell>
                        <TableHeaderCell className="min-w-[11rem] max-w-[15rem]">Empfänger / Schüler</TableHeaderCell>
                        <TableHeaderCell className="min-w-[10rem] max-w-[13rem]" title="Lehrkraft, die die Forderung angelegt hat">
                          Ersteller Forderung
                        </TableHeaderCell>
                        <TableHeaderCell className="min-w-[9rem] max-w-[12rem] whitespace-nowrap">Referenz</TableHeaderCell>
                        {canUseRawWorkflows && (paymentPersona === 'secretariat' || paymentPersona === 'leadership') ? (
                          <TableHeaderCell className="min-w-[11rem] whitespace-nowrap">Aktion</TableHeaderCell>
                        ) : null}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bankTransactions.length === 0 ? (
                        <EmptyTableRow
                          colSpan={canUseRawWorkflows && (paymentPersona === 'secretariat' || paymentPersona === 'leadership') ? 11 : 10}
                          title="Keine Buchungen"
                          description="FinTS-Sync unter «Eingänge» ausführen oder Filter lockern."
                        />
                      ) : (
                        bankTransactions.map((tx) => {
                          const purposeRaw = String(tx.purpose || '').trim();
                          const purposeDisplay = purposeRaw ? softenBankPurposeForDisplay(purposeRaw) : '';
                          const txAmt = toAmount(tx.amount);
                          const amountFormatted = `${txAmt >= 0 ? '+' : ''}${txAmt.toFixed(2)} ${tx.currency || 'EUR'}`;
                          return (
                            <TableRow key={tx.id}>
                              <TableCell className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                {tx.booking_date
                                  ? new Date(tx.booking_date).toLocaleDateString('de-DE')
                                  : '–'}
                              </TableCell>
                              <TableCell
                                align="right"
                                className={`whitespace-nowrap tabular-nums text-sm font-medium ${
                                  txAmt >= 0
                                    ? 'text-emerald-700 dark:text-emerald-400'
                                    : 'text-gray-800 dark:text-gray-200'
                                }`}
                              >
                                {amountFormatted}
                              </TableCell>
                              <TableCell className="min-w-[12rem] max-w-[18rem] whitespace-normal break-words text-sm">
                                {tx.counterpart_name || '–'}
                              </TableCell>
                              <TableCell
                                lang="de"
                                title={purposeRaw || undefined}
                                className="min-w-[26rem] max-w-[36rem] align-top text-xs leading-relaxed text-gray-600 [overflow-wrap:anywhere] xl:min-w-[28rem] dark:text-gray-400"
                              >
                                {purposeDisplay ? (
                                  <div className="max-h-[6rem] overflow-y-auto overscroll-contain pr-0.5 [scrollbar-width:thin]">
                                    {purposeDisplay}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500">–</span>
                                )}
                              </TableCell>
                              <TableCell className="whitespace-nowrap align-top">
                                <Badge variant={giroAllocationBadgeVariant(tx.allocation_state || '')}>
                                  {giroAllocationStateLabel(tx.allocation_state || '')}
                                </Badge>
                              </TableCell>
                              <TableCell className="min-w-[16rem] max-w-[22rem] whitespace-normal text-xs leading-snug text-gray-700 dark:text-gray-300">
                                {(tx.allocation_sources || []).length > 0 ? (
                                  <ul className="space-y-1">
                                    {(tx.allocation_sources || []).map((s, i) => (
                                      <li key={i}>{s.line_de}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500">–</span>
                                )}
                              </TableCell>
                              <TableCell className="min-w-[11rem] max-w-[15rem] whitespace-normal break-words text-sm">
                                {tx.linked_campaign_titles || (
                                  <span className="text-gray-400 dark:text-gray-500">–</span>
                                )}
                              </TableCell>
                              <TableCell className="min-w-[11rem] max-w-[15rem] whitespace-normal break-words text-sm">
                                {tx.linked_recipient_names || (
                                  <span className="text-gray-400 dark:text-gray-500">–</span>
                                )}
                              </TableCell>
                              <TableCell className="min-w-[10rem] max-w-[13rem] whitespace-normal break-words text-xs text-gray-600 dark:text-gray-400">
                                {tx.linked_creator_usernames || (
                                  <span className="text-gray-400 dark:text-gray-500">–</span>
                                )}
                              </TableCell>
                              <TableCell className="min-w-[9rem] max-w-[12rem] whitespace-normal break-all font-mono text-xs text-gray-600 dark:text-gray-400">
                                {tx.linked_reference_codes || (
                                  <span className="text-gray-400 dark:text-gray-500">–</span>
                                )}
                              </TableCell>
                              {canUseRawWorkflows &&
                              (paymentPersona === 'secretariat' || paymentPersona === 'leadership') ? (
                                <TableCell className="whitespace-nowrap">
                                  <div className="flex flex-wrap gap-1">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      disabled={toAmount(tx.amount) <= 0}
                                      title={toAmount(tx.amount) <= 0 ? 'Nur bei Gutschriften zuordenbar' : undefined}
                                      onClick={() => void openBankGiroAllocationEdit(tx)}
                                    >
                                      Zuordnung
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => void openBankFinanceAudit(tx)}
                                    >
                                      Protokoll
                                    </Button>
                                  </div>
                                </TableCell>
                              ) : null}
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableScrollArea>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-4 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-400">
                  <p>
                    {bankTransactionsTotal > 0
                      ? `Zeige ${(bankPage - 1) * bankPageSize + 1}–${Math.min(bankPage * bankPageSize, bankTransactionsTotal)} von ${bankTransactionsTotal}`
                      : 'Keine Einträge'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={bankPage <= 1}
                      onClick={() => setBankPage((p) => Math.max(1, p - 1))}
                    >
                      Zurück
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={bankPage * bankPageSize >= bankTransactionsTotal}
                      onClick={() => setBankPage((p) => p + 1)}
                    >
                      Weiter
                    </Button>
                  </div>
                </div>
              </>
            )}
          </section>

          {paymentPersona === 'secretariat' && (
            <section className="card p-6">
              <ListToolbar
                title="Matching-Vorschläge"
                description="Automatische Zuordnungen prüfen und freigeben oder ablehnen."
                trailing={
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => queryClient.invalidateQueries('allocation-suggestions')}
                  >
                    Aktualisieren
                  </Button>
                }
              />
              {loadingSuggestions ? (
                <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Lädt Vorschläge…</p>
              ) : (allocationSuggestions || []).length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Keine offenen Vorschläge</p>
              ) : (
                <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                  {(allocationSuggestions || []).slice(0, 100).map((suggestion) => (
                    <div key={suggestion.id} className="flex flex-wrap items-start justify-between gap-3 p-4">
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                          <GitPullRequest className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden />
                          <span>
                            {suggestion.recipient_name || 'Unbekannt'} ({suggestion.campaign_title || '-'})
                          </span>
                        </p>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {suggestion.counterpart_name || 'Zahler unbekannt'} | {suggestion.purpose || '-'}
                        </p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                          Ref: {suggestion.reference_code || '-'} | {suggestion.match_method} | Confidence:{' '}
                          {Number(suggestion.confidence || 0).toFixed(3)}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="font-semibold text-emerald-700 dark:text-emerald-400">
                          {toAmount(suggestion.amount).toFixed(2)} EUR
                        </p>
                        <div className="mt-2 flex flex-wrap justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            onClick={() => approveSuggestionMutation.mutate(suggestion.id)}
                          >
                            Freigeben
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="danger"
                            onClick={() => rejectSuggestionMutation.mutate(suggestion.id)}
                          >
                            Ablehnen
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}

      <Modal
        isOpen={Boolean(bankFinanceAuditTx)}
        onClose={() => setBankFinanceAuditTx(null)}
        title={
          bankFinanceAuditTx
            ? `Änderungsprotokoll · ${bankFinanceAuditTx.booking_date ? new Date(bankFinanceAuditTx.booking_date).toLocaleDateString('de-DE') : 'Buchung'} · +${toAmount(bankFinanceAuditTx.amount).toFixed(2)} ${bankFinanceAuditTx.currency || 'EUR'}`
            : 'Änderungsprotokoll'
        }
        size="lg"
      >
        {bankFinanceAuditLoading ? (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">Lade Protokoll…</p>
        ) : bankFinanceAuditEntries.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Noch keine protokollierten Änderungen. Nach Freigabe eines Vorschlags, Speichern in der Zuordnungsmaske oder
            Entfernen einer Zuordnung erscheinen Einträge hier.
          </p>
        ) : (
          <ul className="max-h-[28rem] space-y-3 overflow-auto text-sm">
            {bankFinanceAuditEntries.map((e) => (
              <li
                key={e.id}
                className="rounded-md border border-gray-200 p-3 dark:border-gray-700"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {financeAuditActionLabelDe(e.action)}
                  </span>
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    {e.created_at ? new Date(e.created_at).toLocaleString('de-DE') : ''}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  <span className="font-medium">{e.actor_username || '—'}</span>
                  {e.ip_address ? ` · ${e.ip_address}` : ''}
                </p>
                {e.before_json != null || e.after_json != null ? (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-primary-600 dark:text-primary-400">
                      Technische Details
                    </summary>
                    <pre className="mt-2 max-h-48 overflow-auto rounded bg-gray-50 p-2 text-[10px] leading-relaxed text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                      {JSON.stringify({ before: e.before_json, after: e.after_json }, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(bankGiroEditTx)}
        onClose={() => {
          if (!bankGiroEditSaving) setBankGiroEditTx(null);
        }}
        title="Zahlung Forderungen zuordnen"
        size="lg"
        closeOnBackdrop={!bankGiroEditSaving}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={bankGiroEditSaving}
              onClick={() => setBankGiroEditTx(null)}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={
                bankGiroEditLoading ||
                bankGiroEditSaving ||
                (bankGiroEditTx != null && toAmount(bankGiroEditTx.amount) <= 0)
              }
              onClick={() => void saveBankGiroAllocationEdit()}
            >
              Speichern
            </Button>
          </div>
        }
      >
        {bankGiroEditTx ? (
          <div className="space-y-4 text-sm text-gray-800 dark:text-gray-200">
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800/50">
              <p>
                <span className="text-gray-500 dark:text-gray-400">Buchung: </span>
                {bankGiroEditTx.booking_date
                  ? new Date(bankGiroEditTx.booking_date).toLocaleDateString('de-DE')
                  : '–'}
                {` · +${toAmount(bankGiroEditTx.amount).toFixed(2)} ${bankGiroEditTx.currency || 'EUR'}`}
              </p>
              <p className="mt-1 text-gray-600 dark:text-gray-300">
                {bankGiroEditTx.counterpart_name || 'Zahler unbekannt'}
              </p>
              <p className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400" title={bankGiroEditTx.purpose || ''}>
                {bankGiroEditTx.purpose || '–'}
              </p>
            </div>
            {toAmount(bankGiroEditTx.amount) <= 0 ? (
              <p className="text-amber-700 dark:text-amber-300">Diese Buchung ist keine Gutschrift — manuelle Zuordnung ist nicht vorgesehen.</p>
            ) : null}
            {bankGiroEditLoading ? (
              <p className="text-center text-gray-500">Lade bestehende Zuordnungen…</p>
            ) : (
              <>
                <p className="text-gray-600 dark:text-gray-400">
                  Mehrere Forderungen und Teilbeträge sind möglich. Die Summe darf den Buchungsbetrag nicht
                  überschreiten. Leere Zeilen werden ignoriert; leer speichern entfernt alle Verknüpfungen.
                </p>
                <div className="space-y-3">
                  {bankGiroEditLines.map((line, idx) => (
                    <div
                      key={`bank-giro-line-${idx}`}
                      className="flex flex-col gap-2 border-b border-gray-100 pb-3 last:border-0 dark:border-gray-700 sm:flex-row sm:items-end"
                    >
                      <div className="min-w-0 flex-1">
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                          Forderung
                        </label>
                        <BankDemandPicker
                          demandId={line.demand_id}
                          label={line.label}
                          candidates={bankGiroOpenDemandsPool}
                          loading={bankGiroOpenDemandsLoading}
                          onSelect={(d) =>
                            updateBankGiroLine(idx, {
                              demand_id: d.id,
                              label: `${d.recipient_name || '–'} · ${d.reference_code} · ${d.campaign_title || ''}`
                            })
                          }
                          onClear={() => updateBankGiroLine(idx, { demand_id: 0, label: '' })}
                        />
                      </div>
                      <label className="flex w-full flex-col gap-1 sm:w-32">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Betrag EUR</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="input-field py-1.5 text-sm tabular-nums"
                          value={line.amount}
                          onChange={(e) => updateBankGiroLine(idx, { amount: e.target.value })}
                        />
                      </label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-red-600 dark:text-red-400"
                        onClick={() =>
                          setBankGiroEditLines((prev) => prev.filter((_, i) => i !== idx))
                        }
                        disabled={bankGiroEditSaving}
                      >
                        Zeile entfernen
                      </Button>
                    </div>
                  ))}
                </div>
                {bankGiroEditLines.length === 0 && !bankGiroEditLoading ? (
                  <p className="text-xs text-amber-800 dark:text-amber-200/90">
                    Keine Zuordnungszeilen. «Zeile hinzufügen» nutzen oder «Speichern» wählen — dann werden alle
                    Verknüpfungen zu dieser Buchung entfernt (mit Rückfrage).
                  </p>
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setBankGiroEditLines((prev) => [...prev, { demand_id: 0, amount: '', label: '' }])
                    }
                  >
                    Zeile hinzufügen
                  </Button>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Summe:{' '}
                  <span className="font-medium tabular-nums">
                    {bankGiroEditLines
                      .reduce((s, l) => s + toAmount(l.amount), 0)
                      .toFixed(2)}{' '}
                    EUR
                  </span>
                  {' · '}
                  Buchung:{' '}
                  <span className="font-medium tabular-nums">
                    {Math.abs(toAmount(bankGiroEditTx.amount)).toFixed(2)} EUR
                  </span>
                </p>
              </>
            )}
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={campaignScopeModalOpen}
        onClose={() => {
          if (!saveCampaignMutation.isLoading) setCampaignScopeModalOpen(false);
        }}
        title="Änderungen anwenden"
        size="lg"
        closeOnBackdrop={!saveCampaignMutation.isLoading}
        footer={
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={saveCampaignMutation.isLoading}
              onClick={() => setCampaignScopeModalOpen(false)}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={saveCampaignMutation.isLoading}
              onClick={() => confirmSaveCampaignOnlyNew()}
            >
              Nur für neue Forderungen
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={saveCampaignMutation.isLoading}
              onClick={() => confirmSaveCampaignAllExisting()}
            >
              Alle bestehenden anpassen (E-Mail, PDF, …)
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Es sind bereits Forderungen zu diesem Vorgang erfasst. Sollen Ihre Änderungen (Titel, Text, Fälligkeit,
          Standardbetrag, Mahnung, Vorlage) nur für den Vorgang gespeichert werden und damit vor allem für{' '}
          <strong>neu hinzugefügte Schüler</strong> gelten — ohne bestehende Beträge und ohne erneuten Versand?
        </p>
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
          Oder sollen die Änderungen — soweit möglich — auch auf <strong>bestehende</strong> Forderungen angewendet,
          anschließend ein aktualisiertes <strong>E-Mail</strong> und ein kombiniertes <strong>PDF</strong> ausgelöst
          und eine geplante <strong>Mahnung</strong> für offene Posten mit E-Mail-Adresse eingerichtet werden?
        </p>
      </Modal>

      {demandPickerOpen && demandPickerCampaign && (
        <PaymentDemandStudentPickerModal
          open
          campaignId={demandPickerCampaign.id}
          campaignTitle={demandPickerCampaign.title}
          defaultDueAt={demandPickerCampaign.due_at}
          defaultAmountEuro={
            demandPickerCampaign.id === selectedCampaignId ? demandPickerDefaultAmountEuro : null
          }
          onClose={() => {
            setDemandPickerOpen(false);
            setDemandPickerCampaign(null);
          }}
          onCreated={async () => {
            await queryClient.invalidateQueries(['payment-demands']);
            await queryClient.invalidateQueries('payment-campaigns');
          }}
        />
      )}

      <FintsTanChallengeModal
        open={Boolean(tanToken)}
        contextTitle="Giro-Umsätze (FinTS)"
        decoupled={tanDecoupled}
        challengeText={tanChallengeText || 'Die Bank fordert eine TAN oder Freigabe an.'}
        challengeMediaB64={tanChallengeMediaB64}
        challengeMediaMime={tanChallengeMediaMime}
        challengeHhduc={tanChallengeHhduc}
        bankMediumName={tanBankMediumName}
        procedureHint={tanProcedureHint}
        methodOptions={tanMethodOptions}
        selectedMethod={selectedTanMethod}
        onSelectedMethodChange={setSelectedTanMethod}
        tanValue={tanValue}
        onTanValueChange={setTanValue}
        onSubmit={() => void submitTan()}
        onAbort={abortGiroTanSession}
        submitting={syncingGiro}
      />
    </div>
  );
};

export default PaymentManager;
